import { fromBackendDocumentToFrontend } from '../utils/utils'
import { backendUrl } from '../config'
import { BrowserRouter as Navigate } from 'react-router-dom'
import { store } from '../redux/store'
import { logout } from '../redux/actions/authAction'
import { enqueueSnackbar } from 'notistack'
import i18n from '../i18n'

class BackendClient {
  constructor() {
    this.isSnackbarVisible = false
  }

  get dispatch() {
    return store.dispatch
  }

  saveUser(user) {
    localStorage.setItem('user', JSON.stringify(user))
  }

  getUser() {
    const userString = localStorage.getItem('user')
    return userString ? JSON.parse(userString) : null
  }

  removeUser() {
    localStorage.removeItem('user')
  }

  saveToken(token) {
    localStorage.setItem('authToken', JSON.stringify(token))
  }

  deleteToken() {
    localStorage.removeItem('authToken')
  }

  getToken() {
    // First try to get token from local storage
    const tokenString = localStorage.getItem("access_token"); // Should match key used in verifyLoginOTP
    const tokenVersion = localStorage.getItem("token_version");
    const tokenType = localStorage.getItem("token_type");
    if (tokenString) {
      return {
        access_token: tokenString,
        token_version: tokenVersion,
        token_type: tokenType
      };
    }

    // If not in local storage, try to get from Redux store
    const state = store.getState();
    if (state.auth && state.auth.user && state.auth.user.token) {
      return state.auth.user.token;
    }

    return null;
  }

  authHeader() {
    const token = this.getToken();
    const lang = i18n.language || store.getState()?.user?.appPreferences?.app_language || 'fr';
    const baseHeaders = {
      'Content-Type': 'application/json',
      'Accept-Language': lang,
    };
    if (token && token.access_token) {
      return { ...baseHeaders, Authorization: `Bearer ${token.access_token}` };
    }
    return baseHeaders;
  }

  // Show a snackbar if one isn't already visible
  showSnackbar(message, variant = 'error') {
    console.log('showing snackbar', this.isSnackbarVisible)

    if (!this.isSnackbarVisible) {
      this.isSnackbarVisible = true // Set flag to true
      enqueueSnackbar(message, {
        variant,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        autoHideDuration: null, // Don't auto-hide
        persist: true, // Keep the snackbar visible
        onClose: () => {
          this.isSnackbarVisible = false // Reset flag when snackbar is closed
        }
      })
    }
  }

  async handleResponse(res) {
    if (res.status === 401) {
      console.log('Session has expired or the token is invalid. Logging out.')
      this.dispatch(logout())
      throw new Error('Session expirée')
    }

    if (res.status === 409) {
      const errorData = await res.json()
      const detail = errorData.detail
      // Device limit reached — don't logout, show device management info
      if (detail && typeof detail === 'object' && detail.code === 'max_devices_reached') {
        const err = new Error('Maximum devices reached')
        err.code = 'max_devices_reached'
        err.devices = detail.active_devices
        err.message = detail.message
        throw err
      }
      // Other 409 conflicts — throw with detail message + original detail
      let message = detail
      if (typeof detail === 'object' && detail !== null) {
        message = detail.msg || detail.message || JSON.stringify(detail)
      }
      const err = new Error(message || 'Conflict')
      err.status = 409
      err.detail = detail
      throw err
    }

    if (res.status === 429) {
      const errorData = await res.json()
      const detail = errorData.detail || {}
      if (detail.code === 'rate_limit_exceeded') {
        const err = new Error('Rate limit exceeded')
        err.code = 'rate_limit_exceeded'
        err.feature = detail.feature
        err.used = detail.used
        err.limit = detail.limit
        err.resetsAt = detail.resets_at
        throw err
      }
      if (detail.code === 'doc_access_rate_limit') {
        const err = new Error('Document access rate limit exceeded')
        err.code = 'doc_access_rate_limit'
        throw err
      }
      throw new Error('Too many requests')
    }

    if (res.status === 403) {
      const errorData = await res.json()
      const detail = errorData.detail

      // Plan feature gating — don't logout, just throw with structured info
      if (detail && typeof detail === 'object' && detail.code === 'feature_not_available') {
        const err = new Error('Feature not available on your plan')
        err.code = 'feature_not_available'
        err.feature = detail.feature
        err.plan = detail.plan
        throw err
      }

      // Account suspended — show suspension info, logout
      if (detail && typeof detail === 'object' && detail.code === 'account_suspended') {
        const suspendedUntil = detail.suspended_until ? new Date(detail.suspended_until).toLocaleString() : ''
        window.alert(`Votre compte a été temporairement suspendu jusqu'au ${suspendedUntil}. Raison: ${detail.reason || 'Violation des conditions d\'utilisation'}.`)
        this.dispatch(logout())
        throw new Error('Account suspended')
      }

      // Account terminated — show termination info, logout
      if (detail && typeof detail === 'object' && detail.code === 'account_terminated') {
        window.alert('Votre compte a été définitivement résilié pour violation des conditions d\'utilisation. Contactez saad.lachhab@lawis.ai pour plus d\'informations.')
        this.dispatch(logout())
        throw new Error('Account terminated')
      }

      if (typeof detail === 'string' && detail.includes('Maximum number of concurrent sessions')) {
        window.alert('Vous avez atteint la limite de 4 sessions simultanées. Veuillez fermer une session existante pour en créer une nouvelle.')
        this.dispatch(logout())
      } else if (detail === 'subscription_inactive' || detail === 'User Is not allowed') {
        // Trial expired or subscription inactive — redirect to subscribe page
        window.location.href = '/subscribe'
      } else {
        window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.')
        this.dispatch(logout())
      }
      throw new Error('Unauthorized access')
    }

    if (!res.ok) {
      const errorData = await res.json()
      let detail = errorData.detail
      if (Array.isArray(detail)) {
        detail = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
      } else if (typeof detail === 'object' && detail !== null) {
        detail = detail.msg || detail.message || JSON.stringify(detail)
      }
      throw new Error(detail || `HTTP error! status: ${res.status}`)
    }

    return res
  }

  async get(endpoint) {
    const url = backendUrl + endpoint
    const headers = this.authHeader()

    try {
      const res = await fetch(url, { headers })
      return await this.handleResponse(res)
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        // Network error or server down
        this.showSnackbar(
          i18n.t('network_error_snackbar')
        )
      }
      throw error
    }
  }

  async post(endpoint, body) {
    const url = backendUrl + endpoint
    const headers = this.authHeader()

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: headers || { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      return await this.handleResponse(res)
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        // Network error or server down
        this.showSnackbar(
          i18n.t('network_error_snackbar')
        )
      }
      throw error
    }
  }

  async delete(endpoint, body) {
    const url = backendUrl + endpoint;
    const headers = this.authHeader();

    try {
      const res = await fetch(url, {
        method: 'DELETE',
        headers: headers || { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      return await this.handleResponse(res);
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        // Network error or server down
        this.showSnackbar(
          i18n.t('network_error_snackbar')
        );
      }
      throw error;
    }
  }

  async put(endpoint, body) {
    const url = backendUrl + endpoint;
    const headers = this.authHeader();

    try {
      const res = await fetch(url, {
        method: 'PUT',
        headers: headers || { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      return await this.handleResponse(res);
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        // Network error or server down
        this.showSnackbar(
          i18n.t('network_error_snackbar')
        );
      }
      throw error;
    }
  }

  async patch(endpoint, body) {
    const url = backendUrl + endpoint;
    const headers = this.authHeader();

    try {
      const res = await fetch(url, {
        method: 'PATCH',
        headers: headers || { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      return await this.handleResponse(res);
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        this.showSnackbar(
          i18n.t('network_error_snackbar')
        );
      }
      throw error;
    }
  }

  // async sendMessage(conversationId, messageContent) {
  //   const endpoint = `api/conversation/${conversationId}/message`;
  //   const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  //   const payload = { user_message: messageContent, timezone };
  //   const res = await this.post(endpoint, payload);

  //   if (!res.ok) {
  //     throw new Error(
  //       `Failed to send message: HTTP error! status: ${res.status}`
  //     );
  //   }
  //   return res.json();
  // }

  async updateMessage(conversationId, messageId, content) {
    const endpoint = `api/conversation/${conversationId}/message/${messageId}`; // Updated endpoint URL

    const headers = this.authHeader();
    try {
      const res = await fetch(backendUrl + endpoint, {
        method: 'PUT',
        headers: headers || { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });

      if (res.status === 403) {
        console.log('Unauthorized access');
        this.dispatch(logout());
        window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.');
        throw new Error('Unauthorized access');
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.');
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to update message: HTTP status ${res.status}`
        );
      }

      return await res.json();  // Assuming you need the response from the server
    } catch (error) {
      console.error('Error updating message:', error.message);
      throw error;
    }
  }

  async updateBoarded(userId, isBoarded) {
    const endpoint = 'api/user/update_is_boarded' // API endpoint
    const payload = {
      user_id: userId,
      is_boarded: isBoarded
    }

    try {
      const res = await this.post(endpoint, payload)

      if (!res.ok) {
        const errorData = await res.json()

        // Handle unexpected response formats
        if (Array.isArray(errorData)) {
          throw new Error(
            `Backend returned errors: ${JSON.stringify(errorData)}`
          )
        } else {
          throw new Error(
            errorData.detail ||
            `Failed to update 'is_boarded': HTTP status ${res.status}`
          )
        }
      }

      return await res.json() // Return the success response
    } catch (error) {
      console.error("Error while updating 'is_boarded':", error.message)
      throw error
    }
  }

  async login(email, password) {
    const endpoint = 'api/auth/login';
    const payload = { email, password };

    try {
      // For login, we don't need auth headers
      const res = await fetch(backendUrl + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.status === 403) {
        const errorData = await res.json();
        if (errorData.detail && errorData.detail.includes('Maximum number of concurrent sessions')) {
          window.alert('Vous avez atteint la limite de 3 sessions simultanées. Vos sessions précédentes ont été déconnectées pour permettre cette nouvelle connexion.');
        } else {
          window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.');
        }
        throw new Error('Unauthorized access');
      }

      if (!res.ok) {
        const errorData = await res.json();
        let detail = errorData.detail;
        if (Array.isArray(detail)) {
          detail = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object' && detail !== null) {
          detail = detail.msg || detail.message || JSON.stringify(detail);
        }
        throw {
          status: res.status,
          message: detail || 'Login failed',
          data: errorData
        };
      }

      const data = await res.json();

      // Check if OTP is required (new response format)
      if (data.requires_otp) {
        return data; // Return the OTP requirement response
      }

      // If direct login successful (existing device), save tokens and token version
      if (data.token && data.user) {
        // Save token with all its components
        localStorage.setItem('access_token', data.token.access_token);
        localStorage.setItem('token_type', data.token.token_type);
        
        // Store token version from either token object or user's current device
        const tokenVersion = data.token.token_version || 
                           (data.user.current_device && data.user.current_device.token_version);
        if (tokenVersion) {
          localStorage.setItem('token_version', tokenVersion);
        }
        
        this.saveUser(data.user);
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async requestLoginOTP(email) {
    const endpoint = 'api/auth/request-otp'
    const payload = { email }

    const res = await this.post(endpoint, payload)
    if (!res.ok) {
      const errorData = await res.json()
      throw {
        status: res.status,
        message: errorData.detail || `Failed to request OTP: ${res.status}`
      }
    }

    const data = await res.json()
    return data
  }

  async verifyLoginOTP(email, otp) {
    const endpoint = 'api/auth/verify-otp'
    const payload = { email, otp }

    const res = await this.post(endpoint, payload)
    if (!res.ok) {
      const errorData = await res.json()
      throw {
        status: res.status,
        message: errorData.detail || `Failed to verify OTP: ${res.status}`
      }
    }

    const data = await res.json()

    // Save token and user data
    if (data.token) {
      this.saveToken(data.token)
      this.saveUser(data.user)
    }

    return data
  }

  async resendLoginOTP(email) {
    const endpoint = 'api/auth/resend-otp'
    const payload = { email }

    const res = await this.post(endpoint, payload)
    if (!res.ok) {
      const errorData = await res.json()
      throw {
        status: res.status,
        message: errorData.detail || `Failed to resend OTP: ${res.status}`
      }
    }

    const data = await res.json()
    return data
  }

  async logout() {
    const endpoint = 'api/auth/logout'
    try {
      const res = await this.post(endpoint, {})
      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.detail || 'Logout failed')
      }
      this.deleteToken()
      this.removeUser()
      return res.json()
    } catch (error) {
      console.error('Logout error:', error)
      // Still clear local data even if server logout fails
      this.deleteToken()
      this.removeUser()
      throw error
    }
  }

  // Add to BackendClient class in backendClient.js
  async forgotPassword(email) {
    const endpoint = 'api/auth/forgot-password'
    const hostname = window.location.hostname
    const domain_name = hostname.includes('.lawis.ai')
      ? hostname.split('.')[0]
      : 'app'
    const payload = { email, domain_name }

    const url = backendUrl + endpoint
    const headers = this.authHeader()

    const res = await fetch(url, {
      method: 'POST',
      headers: headers || { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (!res.ok) {
      const errorData = await res.json()
      throw {
        status: res.status,
        message: errorData.detail || `Failed to send password reset email: ${res.status}`
      }
    }

    return res
  }

  async resetPassword(token, newPassword) {
    const endpoint = 'api/auth/reset-password'
    const payload = { token, new_password: newPassword }

    const url = backendUrl + endpoint
    const headers = this.authHeader()

    const res = await fetch(url, {
      method: 'POST',
      headers: headers || { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (!res.ok) {
      const errorData = await res.json()
      throw {
        status: res.status,
        message: errorData.detail ||
        `Failed to reset password: HTTP status ${res.status}`
      }
    }

    return res
  }

  async signUp(email, password, planPrefs = {}) {
    const res = await this.post('api/auth/register', { email, password, ...planPrefs })
    if (!res.ok) {
      const errorData = await res.json()
      const error = new Error(errorData.detail || 'Registration failed.')
      error.status = res.status
      error.data = errorData
      throw error
    }
    return res.json()
  }

  async getUserTypes() {
    const res = await this.get('api/user/user-types')
    return res.json()
  }

  async updateProfile(profileData) {
    const res = await this.patch('api/user/profile', profileData)
    return res.json()
  }

  async createConversation(user_id) {
    const endpoint = 'api/conversation/create'
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const payload = { user_id }
    const headers = {
      ...this.authHeader(),
      'X-Timezone': timezone,
    }
    const res = await fetch(backendUrl + endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const errorData = await res.json()
      throw new Error(
        errorData.detail ||
        `Échec de la création de la conversation : statut HTTP ${res.status}`
      )
    }
    const data = await res.json()
    return data
  }

  async fetchUser(userId) {
    const endpoint = 'api/auth/fetch-user' // API endpoint for fetching user
    const payload = { user_id: userId }

    try {
      const res = await this.post(endpoint, payload) // Reusing the POST method

      if (!res.ok) {
        const errorData = await res.json()

        // Handle unauthorized or token issues
        if (res.status === 401 || res.status === 403) {
          console.error('Token is invalid or expired. Logging out...')
          this.dispatch(logout()) // Dispatch logout action
          throw new Error(errorData.detail || 'Invalid or expired token.')
        }

        // Handle other errors
        throw new Error(
          errorData.detail || `Failed to fetch user: HTTP status ${res.status}`
        )
      }

      // Parse and return the successful response
      const data = await res.json()

      return data
    } catch (error) {
      console.error('Error while fetching user:', error.message)
      // throw error; // Re-throw the error for further handling
    }
  }

  async fetchUsersByOrganization(organizationId) {
    const endpoint = `api/user/organization/${organizationId}`
    const res = await this.get(endpoint)

    if (!res.ok) {
      throw new Error(`Failed to fetch users: HTTP status ${res.status}`)
    }

    const data = await res.json()
    return data
  }

  async shareConversation(conversationId, userIds, userDomain) {
    const endpoint = `api/conversation/share`
    const payload = { conversation_id: conversationId, users_ids: userIds, domain_name: userDomain }
    console.log('payload', payload)
    const res = await this.post(endpoint, payload)

    if (!res.ok) {
      throw new Error(`Failed to share conversation: ${res.status}`)
    }
    return res
  }

  async deleteConversations(conversationIds) {
    const endpoint = `api/conversation/delete`
    const payload = { conversation_ids: conversationIds }
    const res = await this.post(endpoint, payload) // Assuming you have a POST method
    console.log('res', res)
    if (!res.ok) {
      throw new Error(`Failed to delete conversations: ${res.status}`)
    }
    return res
  }

  async fetchUserConversations(user_id, limit = 5, offset = 0) {
    // Request only essential fields from backend - lean=true excludes messages, sources, tool_executions
    const endpoint = `api/conversation/${user_id}/conversations?limit=${limit}&offset=${offset}&lean=true`
    try {
      const res = await this.get(endpoint)

      if (!res.ok) {
        // Handle the error by throwing an exception
        throw new Error(
          `Échec de la récupération des conversations : statut HTTP ${res.status}`
        )
      }

      const data = await res.json()

      return data.map(conversation => ({
        id: conversation.id,
        title: conversation.title || 'New Conversation',
        users: conversation.users || [], // Lean response won't have users, so default to empty
        updated_at: conversation.updated_at,
        created_at: conversation.created_at,
      }))
    } catch (error) {
      console.error(`Erreur : ${error.message}`)
      throw error // Propagate the error to be handled by the caller
    }
  }

  async fetchConversation(id) {
    const endpoint = `api/conversation/${id}`
    const res = await this.get(endpoint)

    if (!res.ok) {
      // throw new Error(
      //   `Failed to fetch conversation: HTTP status ${res.status}`
      // );
      return []
    }

    const data = await res.json()

    // Reverse messages only if first message is from assistant
    const messages =
      data.messages.length > 0 && data.messages[0].role === 'assistant'
        ? data.messages.reverse()
        : data.messages

    return {
      ...data,
      messages,
      documents: fromBackendDocumentToFrontend(data.documents || []),
    }
  }

  async getDocumentSas(containerName, blobName) {
    const endpoint = `api/document/generate-sas`
    const body = { containerName, blobName }
    const res = await this.post(endpoint, body)
    const data = await res.json()
    return data
  }

  async translateHtml(fileUrl, targetLanguage) {
    const endpoint = 'api/translate/html'
    console.log('fileUrl', fileUrl)
    const payload = {
      file_url: fileUrl,
      target_language: targetLanguage
    }

    try {
      const res = await this.post(endpoint, payload)

      if (res.status === 403) {
        console.log(
          'This account is not allowed by admins, please contact support'
        )
        this.dispatch(logout())
        window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.')
        throw new Error('Unauthorized access')
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.')
        this.dispatch(logout())
        throw new Error('Session expirée')
      }

      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(
          errorData.detail ||
          `Failed to translate document: HTTP status ${res.status}`
        )
      }

      return await res.json()
    } catch (error) {
      console.error('Translation error:', error)
      throw error
    }
  }

  async fetchDocuments() {
    const endpoint = `api/document/`
    const res = await this.get(endpoint)
    const data = await res.json()
    const docs = fromBackendDocumentToFrontend(data)
    return docs
  }

  async post_form(endpoint, formData) {
    const url = backendUrl + endpoint
    const res = await fetch(url, {
      method: 'POST',
      body: formData
    })

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`)
    }
    return res
  }

  async upload_documents(files) {
    const endpoint = `api/document/upload`
    const formData = new FormData()

    files.forEach(file => {
      formData.append('files', file)
    })

    const res = await this.post_form(endpoint, formData)
    const data = await res.json()

    const docs = fromBackendDocumentToFrontend(data)
    return docs
  }

  async exportDocument(content, fileType, filename) {
    try {
      // Always convert HTML to PDF, but now content is a URL
      const url = backendUrl + 'api/document/export'
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  
      const response = await fetch(url, {
        method: 'POST',
        headers: this.authHeader(),
        body: JSON.stringify({
          html_url: content, // Pass URL instead of content
          export_to: 'pdf',
          timezone,
          filename
        })
      })
  
      if (response.status === 403) {
        console.log(
          'This account is not allowed by admins, please contact support'
        )
        this.dispatch(logout())
        throw new Error('unauthorized_access')
      }
  
      if (response.status === 401) {
        console.log('Session has expired or the token is invalid')
        this.dispatch(logout())
        throw new Error('session_expired')
      }
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
  
      return await response.blob()
    } catch (error) {
      console.error('Export error:', error)
      throw error
    }
  }

  async sendSupport(
    supportObject,
    supportDescription,
    attachedFile,
    userEmail
  ) {
    const url = `${backendUrl}api/user/submit_support`

    try {
      // Prepare FormData for file upload
      const formData = new FormData()
      formData.append('supportObject', supportObject)
      formData.append('supportDescription', supportDescription)
      formData.append('userEmail', userEmail)

      if (attachedFile) {
        formData.append('attachedFile', attachedFile)
      }

      console.log('formData', formData)

      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`)
      }

      const headers = this.authHeader()

      console.log('headers', headers)
      const res = await fetch(url, {
        method: 'POST',
        // headers: headers, // Ensure appropriate headers for authentication
        body: formData
      })

      console.log('res', res)

      if (res.status === 403) {
        console.log('This account is not allowed by admins, please contact support')
        this.dispatch(logout())
        const errorData = await res.json()
        if (errorData.detail && errorData.detail.includes('Maximum number of concurrent sessions')) {
          window.alert('Vous avez atteint la limite de 3 sessions simultanées. Veuillez vous déconnecter d\'un autre appareil pour continuer.')
        } else {
          window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.')
        }
        throw new Error('Unauthorized access')
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.')
        this.dispatch(logout())
        throw new Error('Session expirée')
      }

      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(
          errorData.detail ||
          `Failed to send support: HTTP status ${res.status}`
        )
      }

      const data = await res.json()
      return data
    } catch (error) {
      console.error('Error while sending support:', error.message)
      throw error
    }
  }

  async exportMessages(messages, export_to, email) {
    const url = backendUrl + 'api/document/export-messages';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const headers = this.authHeader();
    const body = {
      messages,
      export_to,
      timezone,
    };

    if (email) {
      body.email = email; // Add email only if it's present
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: headers || { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (res.status === 403) {
      console.log('This account is not allowed by admins, please contact support');
      this.dispatch(logout());
      window.alert(
        'Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.'
      );
      return;
    }

    if (res.status === 401) {
      console.log('Session has expired or the token is invalid. Logging out.');
      this.dispatch(logout());
      return;
    }

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const blob = await res.blob();
    return blob;
  }

  async exportArtifact(content, format, title = null) {
    const url = backendUrl + 'api/chat/artifacts/export';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const headers = this.authHeader();
    const body = {
      content,
      format,
      timezone,
    };
    if (title) {
      body.title = title;
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: headers || { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (res.status === 403) {
      this.dispatch(logout());
      return;
    }
    if (res.status === 401) {
      this.dispatch(logout());
      return;
    }
    if (!res.ok) {
      throw new Error(`Export failed: ${res.status}`);
    }

    return await res.blob();
  }

  async downloadContract(contractId, format = 'pdf') {
    // Validate format parameter
    if (!['pdf', 'docx'].includes(format)) {
      throw new Error('Invalid format. Must be "pdf" or "docx"');
    }

    try {
      const url = backendUrl + `api/document/download-contract/${contractId}?format=${format}`;
      const headers = this.authHeader();

      const res = await fetch(url, {
        method: 'GET',
        headers: headers,
        responseType: 'blob'
      });

      if (res.status === 403) {
        console.log('This account is not allowed by admins, please contact support');
        this.dispatch(logout());
        window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.');
        throw new Error('Unauthorized access');
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.');
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (res.status === 404) {
        throw new Error('Contract not found');
      }

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const blob = await res.blob();
      return blob;
    } catch (error) {
      console.error('Error downloading contract:', error);
      throw error;
    }
  }


  async exportContract(content, export_to, layoutInfo = null) {
    const url = backendUrl + 'api/document/export-contract';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const headers = this.authHeader();

    // Create the request body with layout information
    const body = {
      content,
      export_to,
      timezone,
    };

    // Add layout information if provided
    if (layoutInfo) {
      body.layoutInfo = layoutInfo;
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: headers || { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (res.status === 403) {
      console.log('This account is not allowed by admins, please contact support');
      this.dispatch(logout());
      window.alert(
        'Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.'
      );
      return;
    }

    if (res.status === 401) {
      console.log('Session has expired or the token is invalid. Logging out.');
      this.dispatch(logout());
      return;
    }

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const blob = await res.blob();
    return blob;
  }

  async downloadDocument(documentPath) {
    const headers = this.authHeader()
    const url =
      backendUrl +
      `api/document/gcp/{document_id}?document_path=${documentPath}`

    const res = await fetch(url, {
      method: 'POST',
      headers: headers || { 'Content-Type': 'application/json' },
      body: ''
    })

    if (res.status === 403) {
      console.log(
        'This account is not allowed by admins, please contact support'
      )
      this.dispatch(logout())
      window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.')
    }

    if (res.status === 401) {
      console.log('Session has expired or the token is invalid. Logging out.')
      // Dispatch the Redux logout action here
      this.dispatch(logout())
    }

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`)
    }

    const blob = await res.blob()
    return blob
  }

  //   DASHBOARD
  async getConversationsAndUsers(organization_name = null) {
    const endpoint = `api/conversation/conversations-and-users`

    const body = {
      organization_name: organization_name || null // Send null if no organization name
    }

    const res = await this.post(endpoint, body) // Use POST method now

    if (!res.ok) {
      throw new Error(
        `Failed to fetch conversations and users: HTTP status ${res.status}`
      )
    }

    const data = await res.json()
    return {
      totalConversations: data.total_conversations,
      totalUsers: data.total_users
    }
  }

  async getConversationsCountByUser(user_id = null) {
    const endpoint = `api/conversation/conversations-count`

    const body = {
      user_id: user_id || null // Send null if no organization name
    }

    try {
      const res = await this.post(endpoint, body)

      if (res.status === 403) {
        console.log('This account is not allowed by admins, please contact support')
        this.dispatch(logout())
        window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.')
        throw new Error('Unauthorized access')
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.')
        this.dispatch(logout())
        throw new Error('Session expirée')
      }

      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(
          errorData.detail || `Failed to fetch conversations count: HTTP status ${res.status}`
        )
      }

      const data = await res.json()
      return {
        totalConversations: data.total_conversations
      }
    } catch (error) {
      console.error('Error fetching conversations count:', error)
      throw error
    }
  }

  // Fetch conversations over time
  async getConversationsOverTime(organization_name = null) {
    const endpoint = 'api/conversation/conversations-over-time'

    const body = {
      organization_name: organization_name || null // Send null if no organization name
    }

    const res = await this.post(endpoint, body) // Use POST method now

    if (!res.ok) {
      throw new Error(
        `Failed to fetch conversations over time: HTTP status ${res.status}`
      )
    }

    const data = await res.json()
    return data.map(conversationData => ({
      id: conversationData.id,
      data: conversationData.data
    }))
  }

  // Fetch total searches and users
  async getSearchesAndUsers(organization_name = null) {
    const endpoint = `api/search/searches-and-users`

    const body = {
      organization_name: organization_name || null // Send null if no organization name
    }

    const res = await this.post(endpoint, body) // Use POST method

    if (!res.ok) {
      throw new Error(
        `Failed to fetch searches and users: HTTP status ${res.status}`
      )
    }

    const data = await res.json()
    return {
      totalSearchs: data.total_searches,
      totalUsersSearchs: data.total_users
    }
  }

  // Fetch searches over time
  async getSearchesOverTime(organization_name = null) {
    const endpoint = 'api/search/searches-over-time'

    const body = {
      organization_name: organization_name || null // Send null if no organization name
    }

    const res = await this.post(endpoint, body) // Use POST method

    if (!res.ok) {
      throw new Error(
        `Failed to fetch searches over time: HTTP status ${res.status}`
      )
    }

    const data = await res.json()
    return data.map(searchData => ({
      id: searchData.id,
      data: searchData.data
    }))
  }

  // Serarch

  async searchUserConversations(user_id, searchTerm) {
    const endpoint = `api/conversation/${user_id}/conversations/search?search_term=${encodeURIComponent(
      searchTerm
    )}`
    const res = await this.get(endpoint)

    if (!res.ok) {
      throw new Error(
        `Failed to search conversations: HTTP status ${res.status}`
      )
    }

    return res
  }

  async search(query, mode, signal, country = 'MA', searchLanguage = null) {
    const endpoint = 'api/search/' // Update the endpoint as needed
    const payload = {
      query,
      mode: mode,
      country: country
    }

    // Include search_language if provided
    if (searchLanguage) {
      payload.search_language = searchLanguage
    }

    try {
      // const res = await this.post(endpoint, payload, { signal });
      const res = await fetch(backendUrl + endpoint, {
        method: 'POST',
        headers: this.authHeader(),
        body: JSON.stringify(payload),
        signal: signal
      });

      if (!res.ok) {
        // Handle specific HTTP status codes
        if (res.status === 404) {
          const error = new Error('Aucun résultat trouvé.');
          error.status = 404;
          throw error;
        } else {
          const errorData = await res.json();
          const error = new Error(errorData.detail || `Failed to search: HTTP status ${res.status}`);
          error.status = res.status;
          throw error;
        }
      }

      return res;
    } catch (error) {
      // If it's an AbortError, we don't want to treat it as a normal error
      if (error.name === 'AbortError') {
        console.log('Search was cancelled');
        // Create a custom response for aborted requests
        const abortResponse = new Response(JSON.stringify({ detail: 'Search cancelled' }), {
          status: 499, // Client Closed Request (non-standard but commonly used)
          headers: { 'Content-Type': 'application/json' }
        });
        return abortResponse;
      }

      console.error('Search error:', error);
      // Re-throw the error to be handled by the caller
      throw error;
    }
  }




  async updateMessageLike(message_id, value) {
    const endpoint = `api/conversation/update-message-like/` // Adjust the endpoint as per your API route

    const payload = {
      message_id: message_id,
      value: value
    }
    const res = await this.post(endpoint, payload)
    const data = await res.json()
    return data
  }

  async getRecentSearches(userId, offset = 0) {
    const endpoint = `api/search/recent?offset=${offset}&user_id=${userId}`
    const res = await this.get(endpoint)
    if (!res.ok)
      throw new Error(`Failed to fetch recent searches: ${res.status}`)
    return res
  }

  async getAutocompleteResults(query) {
    const endpoint = `api/search/autocomplete?query=${encodeURIComponent(
      query
    )}`
    const res = await this.get(endpoint)
    if (!res.ok)
      throw new Error(`Failed to fetch autocomplete results: ${res.status}`)
    return res
  }

  async updatePrompt(userId, type, prompt_text) {
    const endpoint = `api/prompt/update`
    const payload = { user_id: userId, type, prompt_text } // Use user_id and type in the payload

    const headers = this.authHeader()
    const res = await fetch(backendUrl + endpoint, {
      method: 'PUT',
      headers: headers || { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    if (res.status === 403) {
      console.log(
        'This account is not allowed by admins, please contact support'
      )
      this.dispatch(logout())
      window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.')
    }

    if (res.status === 401) {
      console.log('Session has expired or the token is invalid. Logging out.')
      // Dispatch the Redux logout action here
      this.dispatch(logout())
    }

    if (!res.ok) {
      const errorData = await res.json()
      throw {
        status: res.status,
        message:
          errorData.detail ||
          `Failed to update prompt: HTTP status ${res.status}`
      }
    }

    return res.json() // Return the updated prompt
  }

  async getPrompt(userId, promptType = null) {
    const endpoint = `api/prompt/get?user_id=${userId}${promptType ? `&prompt_type=${encodeURIComponent(promptType)}` : ''
      }`

    const res = await this.get(endpoint)

    if (!res.ok) {
      throw new Error(`Failed to fetch prompt: HTTP status ${res.status}`)
    }

    return res.json() // Parse and return the JSON data
  }

  async getContractCategories() {
    const endpoint = 'api/contract/categories/';

    try {
      const res = await this.get(endpoint);

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('No contract categories found');
        } else {
          const errorData = await res.json();
          throw new Error(errorData.detail || `Failed to fetch contract categories: HTTP status ${res.status}`);
        }
      }

      return await res.json();
    } catch (error) {
      console.error('Error while fetching contract categories:', error.message);
      throw error;
    }
  }

  async getUserTemplates(country = null, language = null) {
    let endpoint = 'api/contract/my-templates/';
    const params = new URLSearchParams();
    if (country) params.append('country', country);
    if (language) params.append('language', language);
    if (params.toString()) endpoint += `?${params.toString()}`;

    try {
      const res = await this.get(endpoint);

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('No user templates found');
        } else {
          const errorData = await res.json();
          throw new Error(errorData.detail || `Failed to fetch user templates: HTTP status ${res.status}`);
        }
      }

      return await res.json();
    } catch (error) {
      console.error('Error while fetching user templates:', error.message);
      throw error;
    }
  }

  async getTemplate(templateId) {
    const endpoint = `api/contract/templates/${templateId}`;

    try {
      const res = await this.get(endpoint);

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Template not found');
        }
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch template: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error while fetching template:', error.message);
      throw error;
    }
  }

  async createTemplate(name, description, content) {
    const endpoint = 'api/contract/templates/';
    const payload = {
      name,
      description,
      content,
      is_system: false
    };

    try {
      const res = await this.post(endpoint, payload);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to create template: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error while creating template:', error.message);
      throw error;
    }
  }

  async updateTemplate(templateId, updates) {
    const endpoint = `api/contract/templates/${templateId}`;

    try {
      const res = await this.put(endpoint, updates);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to update template: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error while updating template:', error.message);
      throw error;
    }
  }

  async deleteTemplate(templateId) {
    const endpoint = `api/contract/templates/${templateId}`;

    try {
      const res = await this.delete(endpoint);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to delete template: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error while deleting template:', error.message);
      throw error;
    }
  }

  async createContract(title, templateId, creatorId, description = null, type = "templates", country = null) {
    const endpoint = 'api/contract/';
    const payload = {
      title: title,
      template_id: type === "empty" ? null : templateId,
      creator_id: creatorId,
      description: description,
      type: type,
      country: country
    };

    try {
      const res = await fetch(backendUrl + endpoint, {
        method: 'POST',
        headers: this.authHeader() || { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });


      console.log('XX', res)

      return res.json()
    } catch (error) {
      console.error('Error creating contract:', error);
      throw error;
    }
  }

  async getContractTypes() {
    const endpoint = 'api/contract/contract-types';
    try {
      const res = await this.get(endpoint);
      await this.handleResponse(res);
      return await res.json();
    } catch (error) {
      console.error('Error fetching contract types:', error);
      throw error;
    }
  }

  async generateContract({ title, creatorId, country, language, contractTypeName, category, description, jurisdictionCodes }) {
    const endpoint = 'api/contract/generate';
    const payload = {
      title,
      creator_id: creatorId,
      country,
      language: language || 'fr',
      contract_type_name: contractTypeName,
      contract_category: category || null,
      description: description || null,
      jurisdiction_codes: Array.isArray(jurisdictionCodes) && jurisdictionCodes.length > 0 ? jurisdictionCodes : null,
    };
    try {
      const res = await this.post(endpoint, payload);
      await this.handleResponse(res);
      return await res.json();
    } catch (error) {
      console.error('Error generating contract:', error);
      throw error;
    }
  }

  async importContract(formData) {
    const endpoint = 'api/contract/import';

    try {
      // Get auth headers without content-type to let browser set it with boundary
      const authHeader = this.authHeader();
      const headers = authHeader ?
        { 'Authorization': authHeader.Authorization } :
        {};

      const res = await fetch(backendUrl + endpoint, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      await this.handleResponse(res);
      return await res.json();
    } catch (error) {
      console.error('Error importing contract:', error);
      throw error;
    }
  }

  async getContract(contractId) {
    const endpoint = `api/contract/${contractId}`;

    try {
      const res = await this.get(endpoint);

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Contract not found');
        } else {
          const errorData = await res.json();
          throw new Error(errorData.detail || `Failed to fetch contract: HTTP status ${res.status}`);
        }
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching contract:', error);
      throw error;
    }
  }

  async updateContract(contractId, contractData) {
    const endpoint = `api/contract/${contractId}`;

    try {
      const res = await fetch(backendUrl + endpoint, {
        method: 'PUT',
        headers: this.authHeader() || { 'Content-Type': 'application/json' },
        body: JSON.stringify(contractData)
      });

      await this.handleResponse(res);
      return await res.json();
    } catch (error) {
      console.error('Error updating contract:', error);
      throw error;
    }
  }

  async reorderKanbanContracts(updates) {
    const endpoint = `api/contract/kanban/reorder`;
    try {
      const res = await fetch(backendUrl + endpoint, {
        method: 'PUT',
        headers: this.authHeader() || { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      await this.handleResponse(res);
      return await res.json();
    } catch (error) {
      console.error('Error reordering kanban contracts:', error);
      throw error;
    }
  }

  async shareContract(contractId, users, userDomain = 'app') {
    const endpoint = `api/contract/share`;
    const payload = {
      contract_id: contractId,
      users: users,
      domain_name: userDomain
    };

    try {
      const res = await this.post(endpoint, payload);

      if (res.status === 403) {
        console.log('This account is not allowed by admins, please contact support');
        this.dispatch(logout());
        window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.');
        throw new Error('Unauthorized access');
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.');
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to share contract: HTTP status ${res.status}`
        );
      }

      return await res.json();
    } catch (error) {
      console.error('Error while sharing contract:', error.message);
      throw error;
    }
  }

  async inviteExternalCollaborator(contractId, email, role) {
    const endpoint = `api/contract/invite-external`;
    const payload = { contract_id: contractId, email, role };

    try {
      const res = await this.post(endpoint, payload);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to invite external collaborator: HTTP ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error inviting external collaborator:', error.message);
      throw error;
    }
  }

  async updateCollaboratorRole(contractId, userId, role) {
    const endpoint = `api/contract/roles/update`;
    const payload = {
      contract_id: contractId,
      user_id: userId,
      role: role
    };

    try {
      const res = await this.put(endpoint, payload);

      if (res.status === 403) {
        console.log('This account is not allowed to update collaborator roles');
        window.alert('Vous n\'avez pas l\'autorisation de modifier les rôles des collaborateurs.');
        throw new Error('Unauthorized access');
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.');
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to update collaborator role: HTTP status ${res.status}`
        );
      }

      return await res.json();
    } catch (error) {
      console.error('Error updating collaborator role:', error.message);
      throw error;
    }
  }



  async deleteContracts(contractIds) {
    const endpoint = `api/contract/delete`;
    const payload = { contract_ids: contractIds };

    try {
      const res = await this.post(endpoint, payload);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to delete contracts: HTTP status ${res.status}`
        );
      }

      return await res.json();
    } catch (error) {
      console.error('Error deleting contracts:', error.message);
      throw error;
    }
  }

  // Get user contracts with pagination
  async getUserContracts(userId, limit = null, offset = null) {
    try {
      let url = `api/contract/?user_id=${userId}`;
      if (limit !== null && offset !== null) {
        url += `&limit=${limit}&offset=${offset}`;
      }
      const res = await this.get(url);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch user contracts: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error getting user contracts:', error);
      throw error;
    }
  }

  // Search contracts by term
  async searchContracts(userId, searchTerm) {
    try {
      const res = await this.get(`api/contract/search?user_id=${userId}&search_term=${encodeURIComponent(searchTerm)}`);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to search contracts: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error searching contracts:', error);
      throw error;
    }
  }

  // Get contract count by user
  async getContractsCountByUser(userId) {
    try {
      const res = await this.get(`api/contract/count?user_id=${userId}`);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to get contracts count: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error getting contracts count:', error);
      throw error;
    }
  }
  async getContractsCountUniqueByUser(userId) {
    try {
      const res = await this.get(`api/contract/count-unique?user_id=${userId}`);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to get contracts count: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error getting contracts count:', error);
      throw error;
    }
  }

  // Get contracts filtered by tab with pagination
  async getContractsByTab(tab, limit, offset) {
    try {
      const url = `api/contract/tab-filtered/?tab=${tab}&limit=${limit}&offset=${offset}`;
      const res = await this.get(url);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch contracts by tab: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching contracts by tab:', error);
      throw error;
    }
  }

  // Get total count of contracts for a specific tab
  async getContractsCountByTab(tab) {
    try {
      const res = await this.get(`api/contract/count-by-tab/?tab=${tab}`);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to get contracts count by tab: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error getting contracts count by tab:', error);
      throw error;
    }
  }

  /**
 * Clear all messages from a contract conversation
 * @param {string} conversationId - The UUID of the conversation to clear
 * @returns {Promise<Object>} - Response object
 */
  async clearContractConversation(conversationId) {
    try {
      const endpoint = `api/contract/chat/conversations/${conversationId}/clear`;
      const res = await fetch(backendUrl + endpoint, {
        method: 'DELETE',
        headers: this.authHeader()
      });

      await this.handleResponse(res);
      return await res.json();
    } catch (error) {
      console.error('Error clearing contract conversation:', error);
      throw error;
    }
  }


  async createContractConversation(contractId, userId, timezone = null) {
    try {
      const endpoint = 'api/contract/chat/conversations';
      const userTz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

      const payload = {
        contract_id: contractId,
        user_id: userId,
        timezone: userTz
      };

      const res = await this.post(endpoint, payload);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to create contract conversation: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error creating contract conversation:', error);
      throw error;
    }
  }

  async processPrompt(text, operation, customPrompt = null, contractId = null) {
    const endpoint = 'api/contract/process-prompt';

    const payload = {
      text: text,
      operation: operation,
      custom_prompt: customPrompt,
      contract_id: contractId
    };

    try {
      const res = await this.post(endpoint, payload);

      if (res.status === 403) {
        console.log('This account is not allowed by admins, please contact support');
        this.dispatch(logout());
        window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.');
        throw new Error('Unauthorized access');
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.');
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to process text with AI: HTTP status ${res.status}`
        );
      }

      return await res.json();
    } catch (error) {
      console.error('Error processing text with AI:', error.message);
      throw error;
    }
  }


  // Add these methods to your existing backendClient class


  // Get contracts over time
  async getContractsOverTime(userId) {
    try {
      const endpoint = 'api/contract/over-time';
      const payload = { user_id: userId };

      const res = await this.post(endpoint, payload);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch contracts over time: HTTP status ${res.status}`);
      }

      const data = await res.json();
      return data.map(contractData => ({
        id: contractData.id,
        data: contractData.data
      }));
    } catch (error) {
      console.error('Error fetching contracts over time:', error);
      throw error;
    }
  }

  // Get contracts by status
  async getContractsByStatus(userId) {
    try {
      const endpoint = 'api/contract/by-status';
      const payload = { user_id: userId };

      const res = await this.post(endpoint, payload);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch contracts by status: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching contracts by status:', error);
      throw error;
    }
  }

  // Get contracts by type
  async getContractsByType(userId) {
    try {
      const endpoint = 'api/contract/by-type';
      const payload = { user_id: userId };

      const res = await this.post(endpoint, payload);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch contracts by type: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching contracts by type:', error);
      throw error;
    }
  }

  // Get lifecycle dashboard data
  async getLifecycleDashboard(userId) {
    try {
      const res = await this.get(`api/contract/lifecycle-dashboard?user_id=${userId}`)
      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.detail || `Failed to fetch lifecycle dashboard: HTTP status ${res.status}`)
      }
      return await res.json()
    } catch (error) {
      console.error('Error fetching lifecycle dashboard:', error)
      throw error
    }
  }

  // ── CLM Dashboard endpoints ──────────────────────────────────────────────

  async getDashboardUpcomingDeadlines() {
    try {
      const res = await this.get('api/contract/dashboard/upcoming-deadlines')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return await res.json()
    } catch (error) {
      console.error('Error fetching upcoming deadlines:', error)
      throw error
    }
  }

  async getDashboardSignaturePerformance() {
    try {
      const res = await this.get('api/contract/dashboard/signature-performance')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return await res.json()
    } catch (error) {
      console.error('Error fetching signature performance:', error)
      throw error
    }
  }

  async getDashboardTopCounterparties() {
    try {
      const res = await this.get('api/contract/dashboard/top-counterparties')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return await res.json()
    } catch (error) {
      console.error('Error fetching top counterparties:', error)
      throw error
    }
  }

  async getAlertSummary() {
    try {
      const res = await this.get('api/contract/monitoring/regulatory-alerts/summary')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return await res.json()
    } catch (error) {
      console.error('Error fetching alert summary:', error)
      throw error
    }
  }

  async removeCollaborator(contractId, userId) {
    const endpoint = `api/contract/collaborator`;
    const payload = {
      contract_id: contractId,
      user_id: userId
    };

    try {
      const res = await this.delete(endpoint, payload);

      if (res.status === 403) {
        console.log('This account is not allowed to remove collaborators');
        window.alert('Vous n\'avez pas l\'autorisation de supprimer des collaborateurs.');
        throw new Error('Unauthorized access');
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.');
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to remove collaborator: HTTP status ${res.status}`
        );
      }

      return await res.json();
    } catch (error) {
      console.error('Error removing collaborator:', error.message);
      throw error;
    }
  }


  /**
   * Mark the current user's review as completed for a contract.
   */
  async completeReview(contractId) {
    const endpoint = `api/contract/review/complete`;
    const payload = { contract_id: contractId };

    try {
      const res = await this.post(endpoint, payload);

      if (res.status === 401) {
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to complete review: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error completing review:', error.message);
      throw error;
    }
  }

  /**
   * Reset a reviewer's review status back to pending.
   */
  async resetReview(contractId, userId) {
    const endpoint = `api/contract/review/reset`;
    const payload = {
      contract_id: contractId,
      user_id: userId,
    };

    try {
      const res = await this.post(endpoint, payload);

      if (res.status === 401) {
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to reset review: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error resetting review:', error.message);
      throw error;
    }
  }

  /**
   * Add a signatory to a contract
   */
  async addSignatory(contractId, email, firstName, lastName) {
    try {
      const response = await this.post(`api/contract/signature/add-signatory?contract_id=${contractId}`, {
        email: email,
        first_name: firstName || "",
        last_name: lastName || ""
      });
      return response;
    } catch (error) {
      console.error('Error adding signatory:', error);
      throw error;
    }
  }

  /**
   * Delete a signatory from a contract
   */
  async deleteSignatory(contractId, email) {
    try {
      const response = await this.delete(`api/contract/signature/${contractId}/signatory/${encodeURIComponent(email)}`);
      return response;
    } catch (error) {
      console.error('Error deleting signatory:', error);
      throw error;
    }
  }

  /**
   * Create a signature request
   */
  async createSignatureRequest(contractId, signers, callbackUrl, expirationDays = 7, reminderDays = 3) {
    try {
      const response = await this.post('api/contract/signature/create', {
        contract_id: contractId,
        signers: signers,
        callback_url: callbackUrl,
        expiration_days: expirationDays,
        reminder_days: reminderDays
      });
      return response;
    } catch (error) {
      console.error('Error creating signature request:', error);
      throw error;
    }
  }

  /**
   * Get signature status for a contract
   */
  async getSignatureStatus(contractId) {
    try {
      const response = await this.get(`api/contract/signature/${contractId}/status`);
      return response;
    } catch (error) {
      console.error('Error fetching signature status:', error);
      throw error;
    }
  }

  /**
   * Cancel a signature request
   */
  async cancelSignatureRequest(contractId) {
    try {
      const response = await this.post(`api/contract/signature/${contractId}/cancel`);
      return response;
    } catch (error) {
      console.error('Error cancelling signature request:', error);
      throw error;
    }
  }

  /**
   * Send signature reminder
   */
  async sendSignatureReminder(contractId, message) {
    try {
      const response = await this.post(`api/contract/signature/${contractId}/remind`, {
        message: message
      });
      return response;
    } catch (error) {
      console.error('Error sending signature reminder:', error);
      throw error;
    }
  }

  /**
   * Get contracts where the current user has a pending signature
   */
  async getMyPendingSignatures(limit = 8, offset = 0) {
    try {
      const response = await this.get(`api/contract/signature/my-pending?limit=${limit}&offset=${offset}`);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching my pending signatures:', error);
      throw error;
    }
  }

  /**
   * Get count of contracts where the current user has a pending signature
   */
  async getMyPendingSignaturesCount() {
    try {
      const response = await this.get('api/contract/signature/my-pending/count');
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error getting pending signatures count:', error);
      throw error;
    }
  }

  /**
   * Check if the current user has a pending signature on a contract
   */
  async checkPendingSigner(contractId) {
    try {
      const response = await this.get(`api/contract/signature/${contractId}/check-pending`);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error checking pending signer:', error);
      throw error;
    }
  }

  /**
   * Send OTP for internal signing
   */
  async sendInternalOTP(contractId) {
    try {
      const response = await this.post(`api/contract/signature/${contractId}/internal-send-otp`);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error sending internal OTP:', error);
      throw error;
    }
  }

  /**
   * Verify OTP for internal signing
   */
  async verifyInternalOTP(contractId, otpCode) {
    try {
      const response = await this.post(`api/contract/signature/${contractId}/internal-verify-otp`, {
        otp_code: otpCode
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error verifying internal OTP:', error);
      throw error;
    }
  }

  /**
   * Submit internal signature (authenticated user, requires OTP verification)
   */
  async submitInternalSignature(contractId, signatureData, signatureType) {
    try {
      const response = await this.post(`api/contract/signature/${contractId}/sign`, {
        signature_data: signatureData,
        signature_type: signatureType
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error submitting internal signature:', error);
      throw error;
    }
  }

  async checkUserExists(email) {
    const endpoint = 'api/auth/check-user';
    const payload = { email };

    try {
      const res = await this.post(endpoint, payload);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to check user: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error checking if user exists:', error.message);
      throw error;
    }
  }

  // ── Stripe Checkout ─────────────────────────────────────────────────────────

  async createStripeSession(plan, billingCycle, promoCodeId = null, region = 'default', locale = 'auto', quantity = 1, currency = null) {
    const res = await this.post('api/stripe/create-session', {
      plan,
      billing_cycle: billingCycle,
      promo_code_id: promoCodeId || undefined,
      region: region || 'default',
      locale: locale || 'auto',
      quantity: quantity || 1,
      currency: currency ? String(currency).toLowerCase() : undefined,
    })
    if (!res.ok) {
      const errorData = await res.json()
      const error = new Error(errorData.detail || 'Failed to create checkout session.')
      error.status = res.status
      error.data = errorData
      throw error
    }
    return res.json()
  }

  async getStripeSessionStatus(sessionId) {
    try {
      const res = await this.get(`api/stripe/session-status?session_id=${sessionId}`)
      return await res.json()
    } catch (error) {
      console.error('Error fetching Stripe session status:', error)
      throw error
    }
  }

  // ── Billing management ───────────────────────────────────────────────────────

  async getSubscription() {
    try {
      const res = await this.get('api/billing/subscription')
      return await res.json()
    } catch (error) {
      console.error('Error fetching subscription:', error)
      throw error
    }
  }

  async getBillingHistory() {
    try {
      const res = await this.get('api/billing/history')
      return await res.json()
    } catch (error) {
      console.error('Error fetching billing history:', error)
      throw error
    }
  }

  async downloadInvoice(invoiceId) {
    // Returns the redirect URL to the Stripe-hosted invoice PDF
    return `${backendUrl}api/billing/invoice/${invoiceId}/download`
  }

  async cancelSubscription() {
    try {
      const res = await this.post('api/billing/cancel', {})
      return await res.json()
    } catch (error) {
      console.error('Error cancelling subscription:', error)
      throw error
    }
  }

  async upgradeSubscription(plan, billingCycle) {
    try {
      const res = await this.post('api/billing/upgrade', { plan, billing_cycle: billingCycle })
      return await res.json()
    } catch (error) {
      console.error('Error upgrading subscription:', error)
      throw error
    }
  }

  async getBillingPortalUrl() {
    try {
      const res = await this.get('api/billing/portal')
      return await res.json()
    } catch (error) {
      console.error('Error getting billing portal URL:', error)
      throw error
    }
  }



  // ── Super Admin — Platform Management ────────────────────────────────────────

  async getAdminOrganizations() {
    const res = await this.get('api/admin/organizations');
    return await res.json();
  }

  async createAdminOrganization(data) {
    const res = await this.post('api/admin/organizations', data);
    return await res.json();
  }

  async getAdminOrganization(orgId) {
    const res = await this.get(`api/admin/organizations/${orgId}`);
    return await res.json();
  }

  async updateAdminOrganization(orgId, data) {
    const res = await this.put(`api/admin/organizations/${orgId}`, data);
    return await res.json();
  }

  async deleteAdminOrganization(orgId, hard = false) {
    const res = await this.delete(`api/admin/organizations/${orgId}?hard=${hard}`);
    return await res.json();
  }

  async getAdminUsers(organizationId = null) {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    const res = await this.get(`api/admin/users${params}`);
    return await res.json();
  }

  async inviteAdminUser(data) {
    // Invitation email language follows the UI unless the caller sets it (backend defaults to fr)
    const lang = data.lang || ((i18n.language || '').startsWith('fr') ? 'fr' : 'en');
    const res = await this.post('api/admin/users/invite', { ...data, lang });
    return await res.json();
  }

  async deleteAdminInvitation(invitationId) {
    const res = await this.delete(`api/admin/invitations/${invitationId}`);
    return await res.json();
  }

  async resendAdminInvitation(invitationId, lang = 'en') {
    const res = await this.post(`api/admin/invitations/${invitationId}/resend?lang=${lang}`);
    return await res.json();
  }

  async inviteOrg(data) {
    const res = await this.post('api/admin/organizations/invite-org', data);
    if (!res.ok) {
      const errorData = await res.json();
      const detail = errorData.detail;
      if (res.status === 409 && detail && typeof detail === 'object' && detail.user) {
        const err = new Error(detail.message);
        err.existingUser = {
          email: detail.user.email,
          firstName: detail.user.first_name,
          lastName: detail.user.last_name,
          organizationName: detail.organization?.organization_name,
          subscriptionStatus: detail.organization?.subscription_status,
          subscriptionPlan: detail.organization?.subscription_plan,
        };
        throw err;
      }
      throw new Error(typeof detail === 'string' ? detail : 'Error inviting organization');
    }
    return await res.json();
  }

  async updateAdminUser(userId, data) {
    const res = await this.put(`api/admin/users/${userId}`, data);
    return await res.json();
  }

  async deleteAdminUser(userId) {
    const res = await this.delete(`api/admin/users/${userId}`);
    return await res.json();
  }

  async createAdminSubscription(orgId, data) {
    const res = await this.post(`api/admin/organizations/${orgId}/subscription`, data);
    return await res.json();
  }

  async updateAdminSubscription(orgId, data) {
    const res = await this.put(`api/admin/organizations/${orgId}/subscription`, data);
    return await res.json();
  }


  // ── Marketing Organizations (Devis clients) ─────────────────────────────────

  async getMarketingOrganizations(params = {}) {
    const qp = new URLSearchParams();
    if (params.page) qp.set('page', params.page);
    if (params.page_size) qp.set('page_size', params.page_size);
    if (params.search) qp.set('search', params.search);
    if (params.status) qp.set('status', params.status);
    if (params.sort_by) qp.set('sort_by', params.sort_by);
    if (params.sort_order) qp.set('sort_order', params.sort_order);
    const qs = qp.toString() ? `?${qp.toString()}` : '';
    const res = await this.get(`api/admin/marketing-organizations/${qs}`);
    return await res.json();
  }

  async createMarketingOrganization(data) {
    const res = await this.post('api/admin/marketing-organizations/', data);
    return await res.json();
  }

  async getMarketingOrganization(orgId) {
    const res = await this.get(`api/admin/marketing-organizations/${orgId}`);
    return await res.json();
  }

  async updateMarketingOrganization(orgId, data) {
    const res = await this.put(`api/admin/marketing-organizations/${orgId}`, data);
    return await res.json();
  }

  async deleteMarketingOrganization(orgId) {
    const res = await this.delete(`api/admin/marketing-organizations/${orgId}`);
    return res;
  }

  async linkContactToMarketingOrg(orgId, contactId) {
    const res = await this.post(`api/admin/marketing-organizations/${orgId}/contacts/${contactId}`, {});
    return await res.json();
  }

  async unlinkContactFromMarketingOrg(orgId, contactId) {
    const res = await this.delete(`api/admin/marketing-organizations/${orgId}/contacts/${contactId}`);
    return res;
  }

  // ── Organization Tasks ─────────────────────────────────────────────────────

  async getOrgTasks(orgId, params = {}) {
    const qp = new URLSearchParams();
    if (params.status) qp.set('status', params.status);
    const qs = qp.toString() ? `?${qp.toString()}` : '';
    const res = await this.get(`api/admin/marketing-organizations/${orgId}/tasks${qs}`);
    return await res.json();
  }

  async createOrgTask(orgId, data) {
    const res = await this.post(`api/admin/marketing-organizations/${orgId}/tasks`, data);
    return await res.json();
  }

  async updateOrgTask(orgId, taskId, data) {
    const res = await this.patch(`api/admin/marketing-organizations/${orgId}/tasks/${taskId}`, data);
    return await res.json();
  }

  async deleteOrgTask(orgId, taskId) {
    return await this.delete(`api/admin/marketing-organizations/${orgId}/tasks/${taskId}`);
  }

  // ── Organization Activity ──────────────────────────────────────────────────

  async getOrgActivity(orgId, params = {}) {
    const qp = new URLSearchParams();
    if (params.page) qp.set('page', params.page);
    if (params.page_size) qp.set('page_size', params.page_size);
    const qs = qp.toString() ? `?${qp.toString()}` : '';
    const res = await this.get(`api/admin/marketing-organizations/${orgId}/activity${qs}`);
    return await res.json();
  }

  // ── Organization Email ─────────────────────────────────────────────────────

  async sendOrgEmail(orgId, formData) {
    const url = backendUrl + `api/admin/marketing-organizations/${orgId}/email`;
    const authHeaders = this.authHeader();
    const headers = {};
    if (authHeaders?.Authorization) headers.Authorization = authHeaders.Authorization;
    const res = await fetch(url, { method: 'POST', headers, body: formData });
    return await res.json();
  }

  // ── Pipeline ───────────────────────────────────────────────────────────────

  async getPipeline(params = {}) {
    const qp = new URLSearchParams();
    if (params.status) qp.set('status', params.status);
    if (params.source) qp.set('source', params.source);
    if (params.sort_by) qp.set('sort_by', params.sort_by);
    if (params.page) qp.set('page', params.page);
    if (params.page_size) qp.set('page_size', params.page_size);
    const qs = qp.toString() ? `?${qp.toString()}` : '';
    const res = await this.get(`api/admin/marketing-organizations/pipeline${qs}`);
    return await res.json();
  }

  // ── Upcoming Tasks (Dashboard) ─────────────────────────────────────────────

  async getUpcomingTasks() {
    const res = await this.get('api/admin/marketing-organizations/tasks/upcoming');
    return await res.json();
  }

  // ── Quotes (Devis) ──────────────────────────────────────────────────────────

  async getQuotes(params = {}) {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    if (params.marketing_organization_id) query.set('marketing_organization_id', params.marketing_organization_id);
    const qs = query.toString();
    const res = await this.get(`api/admin/quotes/${qs ? `?${qs}` : ''}`);
    return await res.json();
  }

  async createQuote(data) {
    const res = await this.post('api/admin/quotes/', data);
    return await res.json();
  }

  async getQuote(quoteId) {
    const res = await this.get(`api/admin/quotes/${quoteId}`);
    return await res.json();
  }

  async updateQuote(quoteId, data) {
    const res = await this.put(`api/admin/quotes/${quoteId}`, data);
    return await res.json();
  }

  async updateQuoteStatus(quoteId, status) {
    const res = await this.put(`api/admin/quotes/${quoteId}/status`, { status });
    return await res.json();
  }

  async downloadQuotePdf(quoteId) {
    const res = await this.get(`api/admin/quotes/${quoteId}/pdf`);
    return await res.blob();
  }

  async sendQuoteEmail(quoteId) {
    const res = await this.post(`api/admin/quotes/${quoteId}/send`, {});
    return await res.json();
  }

  async duplicateQuote(quoteId) {
    const res = await this.post(`api/admin/quotes/${quoteId}/duplicate`, {});
    return await res.json();
  }

  async deleteQuote(quoteId) {
    const res = await this.delete(`api/admin/quotes/${quoteId}`);
    return res;
  }


  async resetUserPassword(userId, currentPassword, newPassword) {
    const endpoint = 'api/auth/user/reset-password';
    const payload = {
      user_id: userId,
      current_password: currentPassword,
      new_password: newPassword
    };

    try {
      const res = await this.post(endpoint, payload);

      if (res.status === 403) {
        console.log('This account is not allowed by admins, please contact support');
        this.dispatch(logout());
        window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.');
        throw new Error('Unauthorized access');
      }

      if (res.status === 401) {
        console.log('Session has expired or token is invalid, or the current password is incorrect.');

        // Only logout if it's a token issue, not a password validation issue
        if (res.statusText !== 'Current password is incorrect') {
          this.dispatch(logout());
          }

        const errorData = await res.json();
        throw new Error(errorData.detail || 'Unauthorized');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to reset password: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }


  async updateOrganizationBillingInfo(organizationId, billingData) {
    const endpoint = `api/organization/update-billing/${organizationId}`;

    try {
      const headers = this.authHeader();
      const url = backendUrl + endpoint;

      const res = await fetch(url, {
        method: 'PUT',
        headers: headers || { 'Content-Type': 'application/json' },
        body: JSON.stringify(billingData)
      });

      if (res.status === 403) {
        console.log('This account is not allowed by admins, please contact support');
        this.dispatch(logout());
        window.alert('Utilisateur non autorisé. Veuillez contacter le support sur WhatsApp au +971 5 22 34 86 55.');
        throw new Error('Unauthorized access');
      }

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.');
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to update organization billing information: HTTP status ${res.status}`
        );
      }

      return await res.json();
    } catch (error) {
      console.error('Error while updating organization billing information:', error.message);
      throw error;
    }
  }


  async filter(filterParams, limit = 10, offset = 0) {
    const endpoint = 'api/conversation/filter';

    // Prepare the request body, removing the user_id since it's now taken from the auth token
    const requestBody = {
      filter_params: filterParams,
      limit,
      offset
    };

    try {
      const res = await this.post(endpoint, requestBody);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to filter conversations: HTTP status ${res.status}`
        );
      }

      return await res.json();
    } catch (error) {
      console.error('Error filtering conversations:', error.message);
      throw error;
    }
  }

  async filterContracts(filterParams, limit = 10, offset = 0) {
    const endpoint = 'api/contract/filter';

    // Prepare the request body, removing the user_id since it's now taken from the auth token
    const requestBody = {
      filter_params: filterParams,
      limit,
      offset
    };

    try {
      const res = await this.post(endpoint, requestBody);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to filter conversations: HTTP status ${res.status}`
        );
      }

      return await res.json();
    } catch (error) {
      console.error('Error filtering conversations:', error.message);
      throw error;
    }
  }

  async validatePromoCode(validateData) {
    try {
      const endpoint = 'api/checkout/validate-promo-code/';

      // Use the object that's passed in directly
      const payload = {
        code: validateData.code,
        plan_type: validateData.plan_type,
        subscription_type: validateData.subscription_type,
        amount: parseFloat(validateData.amount)
      };

      // Make the API call using our existing post method
      const response = await this.post(endpoint, payload);

      // Parse and return the response data
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to validate promo code: HTTP status ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error validating promo code:', error);
      throw error;
    }
  }



  // Add these methods to the BackendClient class

  // Get all devices for the current user
  async getUserDevices() {
    try {
      const res = await this.get('api/device/');

      if (res.status === 401) {
        console.log('Session has expired or the token is invalid. Logging out.');
        this.dispatch(logout());
        throw new Error('Session expirée');
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch devices: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching user devices:', error);
      throw error;
    }
  }

  // Disconnect/remove a device
  async removeDevice(deviceId) {
    try {
      const res = await this.delete(`api/device/${deviceId}`);

      if (res.status === 400) {
        // Cannot disconnect current device
        const errorData = await res.json();
        throw new Error(errorData.detail || "Cannot disconnect your current device");
      }

      if (res.status === 404) {
        throw new Error("Device not found");
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to disconnect device: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error disconnecting device:', error);
      throw error;
    }
  }

  // Rename a device
  async renameDevice(params) {
    try {
      const { deviceId, newName } = params;

      const payload = {
        device_id: deviceId,
        new_name: newName
      };

      const res = await this.put('api/device/rename', payload);

      if (res.status === 404) {
        throw new Error("Device not found");
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to rename device: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error renaming device:', error);
      throw error;
    }
  }

  // Add or update these methods in your backendClient class

  // Fetch news list optimized (without content)
  async fetchNews(params = {}) {
    const {
      skip = 0,
      limit = 20,
      category = null,
      country = null,
      region = null,
      search = null,
      featured_only = false,
      days = 30,
      language = null
    } = params;

    let endpoint = `api/legal-news/news?skip=${skip}&limit=${limit}&days=${days}`;

    if (category) {
      endpoint += `&category=${encodeURIComponent(category)}`;
    }

    if (country) {
      const c = Array.isArray(country) ? country.join(',') : country;
      if (c) endpoint += `&country=${encodeURIComponent(c)}`;
    }

    if (region) {
      endpoint += `&region=${encodeURIComponent(region)}`;
    }

    if (search) {
      endpoint += `&search=${encodeURIComponent(search)}`;
    }

    if (featured_only) {
      endpoint += `&featured_only=true`;
    }

    if (language) {
      endpoint += `&language=${encodeURIComponent(language)}`;
    }

    try {
      const res = await this.get(endpoint);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch news: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching news:', error);
      throw error;
    }
  }

  // Fetch single news item with full content
  async fetchNewsItem(newsId) {
    try {
      const res = await this.get(`api/legal-news/news/${newsId}`);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch news item: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching news item:', error);
      throw error;
    }
  }

  async fetchCategories() {
    try {
      const res = await this.get('api/legal-news/news/categories');
      return await res.json();
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  async fetchNewsCountries(language = null) {
    let endpoint = 'api/legal-news/news/countries';
    if (language) endpoint += `?language=${language}`;
    const res = await this.get(endpoint);
    return await res.json();
  }

  async fetchNewsRegions(language = null) {
    let endpoint = 'api/legal-news/news/regions';
    if (language) endpoint += `?language=${language}`;
    const res = await this.get(endpoint);
    return await res.json();
  }

  async fetchUserCountry() {
    const res = await this.get('api/news/user-country');
    return await res.json();
  }

  async fetchBookmarks(params = {}) {
    const { skip = 0, limit = 20, category = null, language = null } = params;
    const queryParams = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString()
    });

    if (category) {
      queryParams.append('category', category);
    }

    if (language) {
      queryParams.append('language', language);
    }

    const endpoint = `api/news/bookmarks?${queryParams.toString()}`;
    const res = await this.get(endpoint);
    return res.json();
  }

  async addBookmark(newsId) {
    try {
      const res = await this.post('api/news/bookmarks', { news_id: newsId });
      return await res.json();
    } catch (error) {
      console.error('Error adding bookmark:', error);
      throw error;
    }
  }

  async removeBookmark(newsId) {
    try {
      await this.delete(`api/news/bookmarks/${newsId}`);
      return { success: true };
    } catch (error) {
      console.error('Error removing bookmark:', error);
      throw error;
    }
  }

  async fetchPreferences() {
    try {
      const res = await this.get('api/news/preferences');
      return await res.json();
    } catch (error) {
      console.error('Error fetching preferences:', error);
      throw error;
    }
  }

  async updatePreferences(preferences) {
    try {
      const res = await this.put('api/news/preferences', preferences);
      return await res.json();
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  }

  async fetchRecommendations(limit = 5) {
    try {
      const res = await this.get(`api/news/recommendations?limit=${limit}`);
      return await res.json();
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      throw error;
    }
  }

  // Template methods
  async createTemplate(name, description, content, category = null) {
    const response = await this.post('api/contract/templates/', {
      name,
      description,
      content,
      category
    });
    return response.data;
  }

  async createTemplateFromImport(file, templateData) {
    // Create a new FormData instance
    const formData = new FormData();

    // Add the file
    if (file instanceof File) {
      formData.append('file', file);
    } else {
      throw new Error('Invalid file data provided');
    }

    // Add form fields
    formData.append('name', templateData.name);
    if (templateData.description) {
      formData.append('description', templateData.description);
    }
    if (templateData.category) {
      formData.append('category', templateData.category);
    }

    // Make the API call
    const response = await fetch(backendUrl + 'api/contract/templates/import/', {
      method: 'POST',
      headers: {
        'Authorization': this.authHeader().Authorization
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Failed to import template: HTTP status ${response.status}`);
    }

    return await response.json();
  }

  async getLanguagePreferences() {
    try {
      const res = await this.get('api/user/app-preferences');
      return await res.json();
    } catch (error) {
      console.error('Error fetching language preferences:', error);
      throw error;
    }
  }

  async updateLanguagePreferences(preferences) {
    try {
      const res = await this.put('api/user/app-preferences', preferences);
      return await res.json();
    } catch (error) {
      console.error('Error updating language preferences:', error);
      throw error;
    }
  }

  async getAppPreferences() {
    try {
      const res = await this.get('api/user/app-preferences');
      return await res.json();
    } catch (error) {
      console.error('Error fetching app preferences:', error);
      throw error;
    }
  }

  async updateAppPreferences(preferences) {
    try {
      const res = await this.put('api/user/app-preferences', preferences);
      return await res.json();
    } catch (error) {
      console.error('Error updating app preferences:', error);
      throw error;
    }
  }

  // BO (Bulletin Officiel) endpoints
  async fetchLatestBO(limit = 20) {
    try {
      const res = await this.get(`api/data/bo/latest-bo?limit=${limit}`);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch latest BO: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching latest BO:', error);
      throw error;
    }
  }

  async fetchBOById(boId) {
    try {
      const res = await this.get(`api/data/bo/${boId}`);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch BO by ID: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching BO by ID:', error);
      throw error;
    }
  }

  async searchBO(query, language = null, limit = 20) {
    try {
      let endpoint = `api/data/bo/search?limit=${limit}`;
      
      if (query) {
        endpoint += `&query=${encodeURIComponent(query)}`;
      }
      
      if (language) {
        endpoint += `&language=${encodeURIComponent(language)}`;
      }

      const res = await this.get(endpoint);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to search BO: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error searching BO:', error);
      throw error;
    }
  }

  async getDataUpdateAnalysis(boId) {
    try {
      const res = await this.get(`api/data/data-update-analysis/${boId}`);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch data update analysis: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching data update analysis:', error);
      throw error;
    }
  }

  async getDocumentDetails(documentId) {
    try {
      const res = await this.get(`api/data/document/${documentId}`);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || `Failed to fetch document details: HTTP status ${res.status}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error fetching document details:', error);
      throw error;
    }
  }

  // ─── Data Update (manual document management) ──────────────────────────────

  async uploadDataUpdateDocument(formData) {
    try {
      const token = this.getToken();
      const headers = {};
      if (token && token.access_token) {
        headers['Authorization'] = `Bearer ${token.access_token}`;
      }
      const res = await fetch(backendUrl + 'api/data-update/upload', {
        method: 'POST',
        headers,
        body: formData,
      });
      return await this.handleResponse(res);
    } catch (error) {
      console.error('Error uploading data update document:', error);
      throw error;
    }
  }

  async submitDataUpdateReview(taskId, metadata, htmlContent) {
    try {
      const res = await this.post(`api/data-update/tasks/${taskId}/review`, {
        metadata,
        html_content: htmlContent,
      });
      return await res.json();
    } catch (error) {
      console.error('Error submitting data update review:', error);
      throw error;
    }
  }

  async searchDataUpdateDocuments(params) {
    try {
      const queryParts = [];
      if (params.query) queryParts.push(`q=${encodeURIComponent(params.query)}`);
      if (params.country_code) queryParts.push(`country_code=${encodeURIComponent(params.country_code)}`);
      if (params.doc_type) queryParts.push(`doc_type=${encodeURIComponent(params.doc_type)}`);
      if (params.language) queryParts.push(`language=${encodeURIComponent(params.language)}`);
      if (params.active_only !== undefined) queryParts.push(`is_active=${params.active_only}`);
      if (params.page) queryParts.push(`page=${params.page}`);
      if (params.page_size) queryParts.push(`page_size=${params.page_size}`);
      const qs = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
      const res = await this.get(`api/data-update/search${qs}`);
      return await res.json();
    } catch (error) {
      console.error('Error searching data update documents:', error);
      throw error;
    }
  }

  async getDataUpdateDocument(documentId, docType) {
    try {
      const qs = docType ? `?doc_type=${encodeURIComponent(docType)}` : '';
      const res = await this.get(`api/data-update/document/${documentId}${qs}`);
      return await res.json();
    } catch (error) {
      console.error('Error fetching data update document:', error);
      throw error;
    }
  }

  async updateDataUpdateDocument(documentId, payload) {
    try {
      const res = await this.put(`api/data-update/document/${documentId}`, payload);
      return await res.json();
    } catch (error) {
      console.error('Error updating data update document:', error);
      throw error;
    }
  }

  async abrogateDataUpdateDocument(documentId, payload) {
    try {
      const res = await this.post(`api/data-update/document/${documentId}/abrogate`, payload);
      return await res.json();
    } catch (error) {
      console.error('Error abrogating data update document:', error);
      throw error;
    }
  }

  async getDataUpdateReferenceData(countryCode) {
    try {
      const res = await this.get(`api/data-update/reference-data/${countryCode}`);
      return await res.json();
    } catch (error) {
      console.error('Error fetching data update reference data:', error);
      throw error;
    }
  }

  async checkDataUpdateDuplicate(params) {
    try {
      const res = await this.post('api/data-update/check-duplicate', params);
      return await res.json();
    } catch (error) {
      console.error('Error checking duplicate:', error);
      throw error;
    }
  }

  // ─── Filtered Search (server-side pagination + filtering) ──────────────────

  async filteredSearch(query, mode, signal, countries = ['MA'], filters = null, page = 1, pageSize = 15, preFilters = null, jurisdictionCodes = null) {
    const endpoint = 'api/search/filtered'
    const countriesArray = Array.isArray(countries) ? countries : [countries]
    const payload = { query, mode, countries: countriesArray, page, page_size: pageSize }
    // search_language removed - backend now queries all available languages for the country
    if (filters) payload.filters = filters
    if (preFilters) payload.pre_filters = preFilters
    if (jurisdictionCodes && jurisdictionCodes.length > 0) payload.jurisdiction_codes = jurisdictionCodes

    try {
      const res = await fetch(backendUrl + endpoint, {
        method: 'POST',
        headers: this.authHeader(),
        body: JSON.stringify(payload),
        signal
      })

      if (!res.ok) {
        if (res.status === 404) {
          const error = new Error('No results found.')
          error.status = 404
          throw error
        }
        const errorData = await res.json()
        const error = new Error(errorData.detail || `Search failed: HTTP ${res.status}`)
        error.status = res.status
        throw error
      }

      return await res.json()
    } catch (error) {
      if (error.name === 'AbortError') {
        return { results: [], facets: {}, total_count: 0, filtered_count: 0, search_id: null, page, page_size: pageSize, total_pages: 0 }
      }
      throw error
    }
  }

  async paginateSearch(searchId, filters = null, page = 1, pageSize = 15, signal = null) {
    const endpoint = 'api/search/paginate'
    const payload = { search_id: searchId, page, page_size: pageSize }
    if (filters) payload.filters = filters

    const res = await fetch(backendUrl + endpoint, {
      method: 'POST',
      headers: this.authHeader(),
      body: JSON.stringify(payload),
      signal
    })

    if (!res.ok) {
      const errorData = await res.json()
      const error = new Error(errorData.detail || `Paginate failed: HTTP ${res.status}`)
      error.status = res.status
      throw error
    }

    return await res.json()
  }

  // ─── Save Search ──────────────────────────────────────────────────────────

  async saveSearch(data) {
    const endpoint = 'api/search/save'
    const res = await this.post(endpoint, data)
    if (!res.ok) throw new Error(`Failed to save search: ${res.status}`)
    return await res.json()
  }

  async getSavedSearches() {
    const endpoint = 'api/search/saved'
    const res = await this.get(endpoint)
    if (!res.ok) throw new Error(`Failed to fetch saved searches: ${res.status}`)
    return await res.json()
  }

  async deleteSavedSearch(id) {
    const endpoint = `api/search/saved/${id}`
    const res = await this.delete(endpoint)
    if (!res.ok) throw new Error(`Failed to delete saved search: ${res.status}`)
    return await res.json()
  }

  // ─── Search Filter Options ───────────────────────────────────────────────

  async getFilterOptions(mode, countryCode) {
    const endpoint = `api/search/filter-options?mode=${encodeURIComponent(mode)}&country_code=${encodeURIComponent(countryCode)}`
    const res = await this.get(endpoint)
    if (!res.ok) throw new Error(`Failed to fetch filter options: ${res.status}`)
    return await res.json()
  }

  async getJurisdictionTree(countryCode) {
    const endpoint = `api/search/jurisdictions?country_code=${encodeURIComponent(countryCode)}`
    const res = await this.get(endpoint)
    if (!res.ok) throw new Error(`Failed to fetch jurisdiction tree: ${res.status}`)
    return await res.json()
  }

  // ─── Search Bookmarks ─────────────────────────────────────────────────────

  async toggleSearchBookmark(documentExternalId, documentType, nodeData = null) {
    const endpoint = 'api/search/bookmark'
    const payload = { document_external_id: documentExternalId, document_type: documentType }
    if (nodeData) payload.node_data = nodeData
    const res = await this.post(endpoint, payload)
    if (!res.ok) throw new Error(`Failed to toggle bookmark: ${res.status}`)
    return await res.json()
  }

  async getSearchBookmarks() {
    const endpoint = 'api/search/bookmarks'
    const res = await this.get(endpoint)
    if (!res.ok) throw new Error(`Failed to fetch bookmarks: ${res.status}`)
    return await res.json()
  }

  async checkSearchBookmarks(documentExternalIds) {
    const endpoint = 'api/search/bookmark/check'
    const res = await this.post(endpoint, { document_external_ids: documentExternalIds })
    if (!res.ok) throw new Error(`Failed to check bookmarks: ${res.status}`)
    return await res.json()
  }

  // ── Legal Notices (Entreprises / Annonces Legales) ─────────────────

  async searchLegalNotices(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.q) queryParams.append('q', params.q)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params.annee_publication) queryParams.append('annee_publication', params.annee_publication.toString())
    if (params.date_from) queryParams.append('date_from', params.date_from)
    if (params.date_to) queryParams.append('date_to', params.date_to)
    if (params.capital_min) queryParams.append('capital_min', params.capital_min.toString())
    if (params.capital_max) queryParams.append('capital_max', params.capital_max.toString())
    ;['type_annonce', 'forme_juridique', 'ville', 'secteur_principal', 'statut_societe'].forEach(key => {
      if (params[key] && params[key].length > 0) {
        params[key].forEach(val => queryParams.append(key, val))
      }
    })
    const res = await this.get(`api/legal-notices/search?${queryParams.toString()}`)
    return await res.json()
  }

  async getLegalNoticeDetail(annonceId) {
    const res = await this.get(`api/legal-notices/${encodeURIComponent(annonceId)}`)
    return await res.json()
  }

  async getCompanyProfile(rc, villeRc) {
    let endpoint = `api/legal-notices/company/${encodeURIComponent(rc)}`
    if (villeRc) endpoint += `?ville_rc=${encodeURIComponent(villeRc)}`
    const res = await this.get(endpoint)
    return await res.json()
  }

  async getLegalNoticesStats() {
    const res = await this.get('api/legal-notices/stats')
    return await res.json()
  }

  async getDataSourcesStats() {
    const res = await this.get('api/data-sources/stats')
    return await res.json()
  }

  async autocompleteLegalNotices(q) {
    const res = await this.get(`api/legal-notices/autocomplete?q=${encodeURIComponent(q)}`)
    return await res.json()
  }
  // ── Marketing Campaign Management ────────────────────────────────────────

  // Dashboard
  async getMarketingDashboard() {
    const res = await this.get('api/marketing/analytics/dashboard')
    return await res.json()
  }

  // Campaigns
  async getMarketingCampaigns(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/marketing/campaigns/${query ? '?' + query : ''}`)
    return await res.json()
  }

  async createMarketingCampaign(data) {
    const res = await this.post('api/marketing/campaigns/', data)
    return await res.json()
  }

  async getMarketingCampaign(id) {
    const res = await this.get(`api/marketing/campaigns/${id}`)
    return await res.json()
  }

  async updateMarketingCampaign(id, data) {
    const res = await this.put(`api/marketing/campaigns/${id}`, data)
    return await res.json()
  }

  async deleteMarketingCampaign(id) {
    return await this.delete(`api/marketing/campaigns/${id}`)
  }

  async scheduleMarketingCampaign(id, scheduledAt) {
    const res = await this.post(`api/marketing/campaigns/${id}/schedule`, { scheduled_at: scheduledAt })
    return await res.json()
  }

  async validateMarketingCampaign(id) {
    const res = await this.get(`api/marketing/campaigns/${id}/validate`)
    return await res.json()
  }

  async getMarketingCampaignEligibleContacts(id, { page = 1, page_size = 50 } = {}) {
    const res = await this.get(`api/marketing/campaigns/${id}/eligible-contacts?page=${page}&page_size=${page_size}`)
    return await res.json()
  }

  async sendMarketingCampaignNow(id) {
    const res = await this.post(`api/marketing/campaigns/${id}/send-now`)
    return await res.json()
  }

  async pauseMarketingCampaign(id) {
    const res = await this.post(`api/marketing/campaigns/${id}/pause`)
    return await res.json()
  }

  async resumeMarketingCampaign(id) {
    const res = await this.post(`api/marketing/campaigns/${id}/resume`)
    return await res.json()
  }

  // Campaign Analytics
  async getMarketingCampaignStats(id) {
    const res = await this.get(`api/marketing/analytics/campaigns/${id}/stats`)
    return await res.json()
  }

  async getMarketingCampaignFunnel(id) {
    const res = await this.get(`api/marketing/analytics/campaigns/${id}/funnel`)
    return await res.json()
  }

  async getMarketingCampaignABResults(id) {
    const res = await this.get(`api/marketing/analytics/campaigns/${id}/ab-results`)
    return await res.json()
  }

  async getMarketingCampaignSends(id, params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/marketing/analytics/campaigns/${id}/sends${query ? '?' + query : ''}`)
    return await res.json()
  }

  // Sequences
  async getAllMarketingSequences(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/marketing/sequences${query ? '?' + query : ''}`)
    return await res.json()
  }

  async getMarketingSequence(id) {
    const res = await this.get(`api/marketing/sequences/${id}`)
    return await res.json()
  }

  async getMarketingSequences(campaignId) {
    const res = await this.get(`api/marketing/campaigns/${campaignId}/sequences`)
    return await res.json()
  }

  async createMarketingSequence(campaignId, data) {
    const res = await this.post(`api/marketing/campaigns/${campaignId}/sequences`, data)
    return await res.json()
  }

  async updateMarketingSequence(id, data) {
    const res = await this.put(`api/marketing/sequences/${id}`, data)
    return await res.json()
  }

  async deleteMarketingSequence(id) {
    return await this.delete(`api/marketing/sequences/${id}`)
  }

  async createMarketingSequenceStep(sequenceId, data) {
    const res = await this.post(`api/marketing/sequences/${sequenceId}/steps`, data)
    return await res.json()
  }

  async updateMarketingSequenceStep(sequenceId, stepId, data) {
    const res = await this.put(`api/marketing/sequences/${sequenceId}/steps/${stepId}`, data)
    return await res.json()
  }

  async deleteMarketingSequenceStep(sequenceId, stepId) {
    return await this.delete(`api/marketing/sequences/${sequenceId}/steps/${stepId}`)
  }

  // Templates
  async getMarketingTemplates(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/marketing/templates/${query ? '?' + query : ''}`)
    return await res.json()
  }

  async createMarketingTemplate(data) {
    const res = await this.post('api/marketing/templates/', data)
    return await res.json()
  }

  async getMarketingTemplate(id) {
    const res = await this.get(`api/marketing/templates/${id}`)
    return await res.json()
  }

  async updateMarketingTemplate(id, data) {
    const res = await this.put(`api/marketing/templates/${id}`, data)
    return await res.json()
  }

  async deleteMarketingTemplate(id) {
    return await this.delete(`api/marketing/templates/${id}`)
  }

  async previewMarketingTemplate(id, mergeFields = {}) {
    const res = await this.post(`api/marketing/templates/${id}/preview`, mergeFields)
    return await res.json()
  }

  async testSendMarketingTemplate(id, recipient, mergeFields = {}) {
    const res = await this.post(`api/marketing/templates/${id}/test-send`, { recipient, merge_fields: mergeFields })
    return await res.json()
  }

  async duplicateMarketingTemplate(id) {
    const res = await this.post(`api/marketing/templates/${id}/duplicate`)
    return await res.json()
  }

  // Contacts
  async getMarketingContacts(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/marketing/contacts/${query ? '?' + query : ''}`)
    return await res.json()
  }

  async createMarketingContact(data) {
    const res = await this.post('api/marketing/contacts/', data)
    return await res.json()
  }

  async getMarketingContact(id) {
    const res = await this.get(`api/marketing/contacts/${id}`)
    return await res.json()
  }

  async updateMarketingContact(id, data) {
    const res = await this.put(`api/marketing/contacts/${id}`, data)
    return await res.json()
  }

  async deleteMarketingContact(id) {
    return await this.delete(`api/marketing/contacts/${id}`)
  }

  async getMarketingContactTimeline(id, params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/marketing/contacts/${id}/timeline${query ? '?' + query : ''}`)
    return await res.json()
  }

  async importMarketingContacts(file, segmentId) {
    const formData = new FormData()
    formData.append('file', file)
    if (segmentId) formData.append('segment_id', segmentId)
    const url = backendUrl + 'api/marketing/contacts/import'
    const authHeaders = this.authHeader()
    // Remove Content-Type so browser sets multipart/form-data with boundary
    const headers = {}
    if (authHeaders?.Authorization) headers.Authorization = authHeaders.Authorization
    const res = await fetch(url, { method: 'POST', headers, body: formData })
    return await res.json()
  }

  async getMarketingImports(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/marketing/contacts/imports${query ? '?' + query : ''}`)
    return await res.json()
  }

  // Segments
  async getMarketingSegments() {
    const res = await this.get('api/marketing/segments/')
    return await res.json()
  }

  async createMarketingSegment(data) {
    const res = await this.post('api/marketing/segments/', data)
    return await res.json()
  }

  async updateMarketingSegment(id, data) {
    const res = await this.put(`api/marketing/segments/${id}`, data)
    return await res.json()
  }

  async deleteMarketingSegment(id) {
    return await this.delete(`api/marketing/segments/${id}`)
  }

  async addContactsToSegment(segmentId, contactIds) {
    const res = await this.post(`api/marketing/segments/${segmentId}/contacts`, { contact_ids: contactIds })
    return res
  }

  async removeContactsFromSegment(segmentId, contactIds) {
    return await this.delete(`api/marketing/segments/${segmentId}/contacts`, { contact_ids: contactIds })
  }

  // Automations
  async getMarketingAutomations(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/marketing/automations/${query ? '?' + query : ''}`)
    return await res.json()
  }

  async createMarketingAutomation(data) {
    const res = await this.post('api/marketing/automations/', data)
    return await res.json()
  }

  async updateMarketingAutomation(id, data) {
    const res = await this.put(`api/marketing/automations/${id}`, data)
    return await res.json()
  }

  async deleteMarketingAutomation(id) {
    return await this.delete(`api/marketing/automations/${id}`)
  }

  async activateMarketingAutomation(id) {
    const res = await this.post(`api/marketing/automations/${id}/activate`)
    return await res.json()
  }

  async pauseMarketingAutomation(id) {
    const res = await this.post(`api/marketing/automations/${id}/pause`)
    return await res.json()
  }

  // Scoring
  async getMarketingScoringRules() {
    const res = await this.get('api/marketing/scoring/rules')
    return await res.json()
  }

  async createMarketingScoringRule(data) {
    const res = await this.post('api/marketing/scoring/rules', data)
    return await res.json()
  }

  async updateMarketingScoringRule(id, data) {
    const res = await this.put(`api/marketing/scoring/rules/${id}`, data)
    return await res.json()
  }

  async deleteMarketingScoringRule(id) {
    return await this.delete(`api/marketing/scoring/rules/${id}`)
  }

  async recalculateMarketingScores() {
    const res = await this.post('api/marketing/scoring/recalculate')
    return await res.json()
  }

  // WhatsApp
  async getMarketingWhatsAppLogs(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/marketing/whatsapp/${query ? '?' + query : ''}`)
    return await res.json()
  }

  async createMarketingWhatsAppLog(data) {
    const res = await this.post('api/marketing/whatsapp/', data)
    return await res.json()
  }

  async updateMarketingWhatsAppLog(id, data) {
    const res = await this.put(`api/marketing/whatsapp/${id}`, data)
    return await res.json()
  }

  async deleteMarketingWhatsAppLog(id) {
    return await this.delete(`api/marketing/whatsapp/${id}`)
  }

  // ─── BO Consolidation ──────────────────────────────────────────────────────

  async getBoConsolidationList(page = 1, pageSize = 20, status = 'all', search = '', language = 'all', dateFrom = '', dateTo = '') {
    let qs = `?page=${page}&page_size=${pageSize}&status=${status}&search=${encodeURIComponent(search)}&language=${language}`
    if (dateFrom) qs += `&date_from=${dateFrom}`
    if (dateTo) qs += `&date_to=${dateTo}`
    const res = await this.get(`api/bo-consolidation/bo-list${qs}`)
    return await res.json()
  }

  async startBoCleaning(boDbId) {
    const res = await this.post(`api/bo-consolidation/${boDbId}/clean`, {})
    return await res.json()
  }

  async getBoExtractedDocuments(boDbId) {
    const res = await this.get(`api/bo-consolidation/${boDbId}/extracted-documents`)
    return await res.json()
  }

  async getBoExtractedDocPreview(docId) {
    const res = await this.get(`api/bo-consolidation/extracted-document/${docId}/preview`)
    return await res.json()
  }

  async updateBoDocValidation(docId, validationStatus) {
    const res = await this.patch(`api/bo-consolidation/extracted-document/${docId}`, { validation_status: validationStatus })
    return await res.json()
  }

  async updateBoExtractedDoc(docId, { metadata_json, html_content, title, doc_type, doc_number }) {
    const res = await this.put(`api/bo-consolidation/extracted-document/${docId}`, {
      metadata_json, html_content, title, doc_type, doc_number,
    })
    return await res.json()
  }

  async startBoSinglePreview(docId) {
    const res = await this.post(`api/bo-consolidation/extracted-document/${docId}/preview`, {})
    return await res.json()
  }

  async startBoPreviewAll(boDbId) {
    const res = await this.post(`api/bo-consolidation/${boDbId}/preview-all`, {})
    return await res.json()
  }

  async getBoConsolidationPreview(docId) {
    const res = await this.get(`api/bo-consolidation/extracted-document/${docId}/consolidation-preview`)
    return await res.json()
  }

  async confirmBoConsolidationPreview(docId, { merged_html, metadata_json }) {
    const res = await this.put(`api/bo-consolidation/extracted-document/${docId}/consolidation-preview`, {
      merged_html, metadata_json,
    })
    return await res.json()
  }

  async approveAllBoDocs(boDbId, nextStep) {
    const res = await this.post(`api/bo-consolidation/${boDbId}/approve-all`, { next_step: nextStep })
    return await res.json()
  }

  async startBoExecution(boDbId) {
    const res = await this.post(`api/bo-consolidation/${boDbId}/execute`, {})
    return await res.json()
  }

  async getBoPdfUrl(boDbId) {
    const res = await this.get(`api/bo-consolidation/${boDbId}/pdf-url`)
    return await res.json()
  }

  async syncBoFromSgg() {
    const res = await this.post('api/bo-consolidation/sync-sgg', {})
    return await res.json()
  }

  // ── LinkedIn Outreach ──────────────────────────────────────────────────

  // Dashboard
  async getLIOutreachDashboard() {
    const res = await this.get('api/linkedin-outreach/analytics/dashboard')
    return await res.json()
  }
  async getLIOutreachTimeseries({ days = 30, account_id = null } = {}) {
    const qs = new URLSearchParams({ days: String(days) })
    if (account_id) qs.set('account_id', account_id)
    const res = await this.get(`api/linkedin-outreach/analytics/dashboard/timeseries?${qs.toString()}`)
    return await res.json()
  }
  async getLIOutreachAccountsHealth() {
    const res = await this.get('api/linkedin-outreach/analytics/dashboard/accounts-health')
    return await res.json()
  }
  async getLIOutreachCampaignsSummary() {
    const res = await this.get('api/linkedin-outreach/analytics/dashboard/campaigns-summary')
    return await res.json()
  }

  // Accounts
  async getLIProxyLocations() {
    const res = await this.get('api/linkedin-outreach/accounts/locations')
    return await res.json()
  }
  async getLIAccounts() {
    const res = await this.get('api/linkedin-outreach/accounts/')
    return await res.json()
  }
  async getLIAccount(id) {
    const res = await this.get(`api/linkedin-outreach/accounts/${id}`)
    return await res.json()
  }
  async createLIAccount(data) {
    const res = await this.post('api/linkedin-outreach/accounts/', data)
    return await res.json()
  }
  async updateLIAccount(id, data) {
    const res = await this.put(`api/linkedin-outreach/accounts/${id}`, data)
    return await res.json()
  }
  async deleteLIAccount(id) {
    return await this.delete(`api/linkedin-outreach/accounts/${id}`)
  }
  async testLIAccountConnection(id) {
    const res = await this.post(`api/linkedin-outreach/accounts/${id}/test-connection`)
    return await res.json()
  }
  async refreshLIAccountCookies(id) {
    const res = await this.post(`api/linkedin-outreach/accounts/${id}/refresh-cookies`)
    return await res.json()
  }
  async submitLIVerificationCode(id, code) {
    const res = await this.post(`api/linkedin-outreach/accounts/${id}/submit-verification-code`, { code })
    if (!res.ok) {
      // Backend returns 409 with {"detail": {"error": "no_active_session", ...}}
      // when the Playwright pod has timed out. Surface that as a typed error
      // so the UI can show a "Click Refresh" hint instead of a generic failure.
      let payload = null
      try { payload = await res.json() } catch (_) {}
      const detail = payload && payload.detail
      const errorCode = detail && detail.error
      const message = (detail && (detail.message || detail.error)) || `HTTP ${res.status}`
      const err = new Error(message)
      err.status = res.status
      err.errorCode = errorCode
      throw err
    }
    return await res.json()
  }
  async confirmLIAppVerification(id) {
    const res = await this.post(`api/linkedin-outreach/accounts/${id}/confirm-app-verification`)
    if (!res.ok) {
      let payload = null
      try { payload = await res.json() } catch (_) {}
      const detail = payload && payload.detail
      const errorCode = detail && detail.error
      const message = (detail && (detail.message || detail.error)) || `HTTP ${res.status}`
      const err = new Error(message)
      err.status = res.status
      err.errorCode = errorCode
      throw err
    }
    return await res.json()
  }
  async getLIVerificationStatus(id) {
    const res = await this.get(`api/linkedin-outreach/accounts/${id}/verification-status`)
    return await res.json()
  }

  // ICPs
  async getLIICPs() {
    const res = await this.get('api/linkedin-outreach/icps/')
    return await res.json()
  }
  async createLIICP(data) {
    const res = await this.post('api/linkedin-outreach/icps/', data)
    return await res.json()
  }
  async updateLIICP(id, data) {
    const res = await this.put(`api/linkedin-outreach/icps/${id}`, data)
    return await res.json()
  }
  async deleteLIICP(id) {
    return await this.delete(`api/linkedin-outreach/icps/${id}`)
  }
  async generateLIICPFormulas(id) {
    const res = await this.post(`api/linkedin-outreach/icps/${id}/generate-formulas`)
    return await res.json()
  }
  async runLIICPDiscovery(id) {
    const res = await this.post(`api/linkedin-outreach/icps/${id}/run-discovery`)
    return await res.json()
  }

  // Leads
  async getLILeads(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/linkedin-outreach/leads/${query ? '?' + query : ''}`)
    return await res.json()
  }
  async createLILead(data) {
    const res = await this.post('api/linkedin-outreach/leads/', data)
    return await res.json()
  }
  async updateLILead(id, data) {
    const res = await this.put(`api/linkedin-outreach/leads/${id}`, data)
    return await res.json()
  }
  async deleteLILead(id) {
    return await this.delete(`api/linkedin-outreach/leads/${id}`)
  }
  async importLILeads(data) {
    const res = await this.post('api/linkedin-outreach/leads/import', data)
    return await res.json()
  }
  async getLIImports() {
    const res = await this.get('api/linkedin-outreach/leads/imports/list')
    return await res.json()
  }
  async validateLILeads(leadIds, icpId) {
    const params = new URLSearchParams()
    if (leadIds) leadIds.forEach(id => params.append('lead_ids', id))
    if (icpId) params.set('icp_id', icpId)
    const res = await this.post(`api/linkedin-outreach/leads/validate?${params.toString()}`)
    return await res.json()
  }

  // Campaigns
  async getLICampaigns(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/linkedin-outreach/campaigns/${query ? '?' + query : ''}`)
    return await res.json()
  }
  async getLICampaign(id) {
    const res = await this.get(`api/linkedin-outreach/campaigns/${id}`)
    return await res.json()
  }
  async createLICampaign(data) {
    const res = await this.post('api/linkedin-outreach/campaigns/', data)
    return await res.json()
  }
  async updateLICampaign(id, data) {
    const res = await this.put(`api/linkedin-outreach/campaigns/${id}`, data)
    return await res.json()
  }
  async deleteLICampaign(id) {
    return await this.delete(`api/linkedin-outreach/campaigns/${id}`)
  }
  async activateLICampaign(id) {
    const res = await this.post(`api/linkedin-outreach/campaigns/${id}/activate`)
    return await res.json()
  }
  async pauseLICampaign(id) {
    const res = await this.post(`api/linkedin-outreach/campaigns/${id}/pause`)
    return await res.json()
  }
  async addLeadsToLICampaign(id, data) {
    const res = await this.post(`api/linkedin-outreach/campaigns/${id}/leads`, data)
    return await res.json()
  }
  async getLICampaignLeads(id, params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/linkedin-outreach/campaigns/${id}/leads${query ? '?' + query : ''}`)
    return await res.json()
  }
  async getLICampaignStats(id) {
    const res = await this.get(`api/linkedin-outreach/analytics/campaigns/${id}/stats`)
    return await res.json()
  }

  // Sequence Steps
  async getLISteps(campaignId) {
    const res = await this.get(`api/linkedin-outreach/campaigns/${campaignId}/steps`)
    return await res.json()
  }
  async createLIStep(campaignId, data) {
    const res = await this.post(`api/linkedin-outreach/campaigns/${campaignId}/steps`, data)
    return await res.json()
  }
  async updateLIStep(campaignId, stepId, data) {
    const res = await this.put(`api/linkedin-outreach/campaigns/${campaignId}/steps/${stepId}`, data)
    return await res.json()
  }
  async deleteLIStep(campaignId, stepId) {
    return await this.delete(`api/linkedin-outreach/campaigns/${campaignId}/steps/${stepId}`)
  }

  // Inbox
  async getLIConversations(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/linkedin-outreach/inbox/conversations${query ? '?' + query : ''}`)
    return await res.json()
  }
  async getLIConversation(id) {
    const res = await this.get(`api/linkedin-outreach/inbox/conversations/${id}`)
    return await res.json()
  }
  async sendLIReply(conversationId, data) {
    const res = await this.post(`api/linkedin-outreach/inbox/conversations/${conversationId}/reply`, data)
    return await res.json()
  }
  async updateLIConversation(id, data) {
    const res = await this.put(`api/linkedin-outreach/inbox/conversations/${id}`, data)
    return await res.json()
  }

  // Queue & Log
  async getLIQueue(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/linkedin-outreach/queue${query ? '?' + query : ''}`)
    return await res.json()
  }
  async getLIActionLog(params = {}) {
    const query = new URLSearchParams(params).toString()
    const res = await this.get(`api/linkedin-outreach/log${query ? '?' + query : ''}`)
    return await res.json()
  }
  async cancelLIAction(actionId) {
    return await this.delete(`api/linkedin-outreach/queue/${actionId}`)
  }

  // Account daily stats
  async getLIAccountStats(accountId, days = 7) {
    const res = await this.get(`api/linkedin-outreach/analytics/accounts/${accountId}/daily-stats?days=${days}`)
    return await res.json()
  }
  // ── Gmail Inbox ─────────────────────────────────────────────────────────

  async getInboxThreads(params = {}) {
    const qp = new URLSearchParams()
    if (params.folder) qp.set('folder', params.folder)
    if (params.search) qp.set('search', params.search)
    if (params.is_read !== undefined) qp.set('is_read', params.is_read)
    if (params.is_starred !== undefined) qp.set('is_starred', params.is_starred)
    if (params.organization_id) qp.set('organization_id', params.organization_id)
    if (params.page) qp.set('page', params.page)
    if (params.page_size) qp.set('page_size', params.page_size)
    const qs = qp.toString() ? `?${qp.toString()}` : ''
    const res = await this.get(`api/inbox/threads${qs}`)
    return await res.json()
  }

  async getInboxThread(threadId) {
    const res = await this.get(`api/inbox/threads/${threadId}`)
    return await res.json()
  }

  async updateInboxThread(threadId, data) {
    const res = await this.put(`api/inbox/threads/${threadId}`, data)
    return await res.json()
  }

  async sendInboxEmail(data) {
    const res = await this.post('api/inbox/send', data)
    return await res.json()
  }

  async sendInboxEmailWithAttachments(formData) {
    const url = backendUrl + 'api/inbox/send-with-attachments'
    const headers = {}
    const authHeaders = this.authHeader()
    if (authHeaders?.Authorization) headers.Authorization = authHeaders.Authorization
    const res = await fetch(url, { method: 'POST', headers, body: formData })
    return await res.json()
  }

  async replyToThread(threadId, data) {
    const res = await this.post(`api/inbox/threads/${threadId}/reply`, data)
    return await res.json()
  }

  async replyToThreadWithAttachments(threadId, formData) {
    const url = backendUrl + `api/inbox/threads/${threadId}/reply-with-attachments`
    const headers = {}
    const authHeaders = this.authHeader()
    if (authHeaders?.Authorization) headers.Authorization = authHeaders.Authorization
    const res = await fetch(url, { method: 'POST', headers, body: formData })
    return await res.json()
  }

  async getInboxStats() {
    const res = await this.get('api/inbox/stats')
    return await res.json()
  }

  async getSignature() {
    const res = await this.get('api/inbox/settings/signature')
    return await res.json()
  }

  async updateSignature(data) {
    const res = await this.put('api/inbox/settings/signature', data)
    return await res.json()
  }

  async suggestInboxContacts(query) {
    const res = await this.get(`api/inbox/contacts/suggest?q=${encodeURIComponent(query)}`)
    return await res.json()
  }

  async triggerInboxSync() {
    const res = await this.post('api/inbox/sync', {})
    return await res.json()
  }

  async downloadInboxAttachment(threadId, messageId, attachmentId) {
    return await this.get(`api/inbox/threads/${threadId}/messages/${messageId}/attachments/${attachmentId}`)
  }

  async linkOrgToThread(threadId, formData) {
    const url = backendUrl + `api/inbox/threads/${threadId}/link-organization`
    const headers = {}
    const authHeaders = this.authHeader()
    if (authHeaders?.Authorization) headers.Authorization = authHeaders.Authorization
    const res = await fetch(url, { method: 'POST', headers, body: formData })
    return await res.json()
  }

  async getInboxSyncStatus() {
    const res = await this.get('api/inbox/settings/sync-status')
    return await res.json()
  }
}

export const backendClient = new BackendClient()